<template>
  <div class="min-h-screen bg-white text-color-gray-800 font-sans" style="width: 100% !important; max-width: 100% !important; overflow-x: hidden !important;">
    <!-- Password Overlay -->
    <div v-if="!isPasswordCorrect" class="fixed inset-0 bg-white z-50 flex items-center justify-center">
      <div class="w-full max-w-md p-4">
        <div class="text-center mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-2">Доступ к сервису</h2>
          <p class="text-gray-600">Введите пароль для доступа к сервису</p>
        </div>
        <div class="space-y-4">
          <div>
            <input 
              v-model="passwordInput" 
              type="password" 
              placeholder="Введите пароль"
              class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 transition-colors duration-150 py-2.5 px-3.5 input-ios"
              @keyup.enter="checkPassword"
            >
          </div>
          <div class="flex items-center">
            <input 
              type="checkbox" 
              id="remember-me" 
              v-model="rememberMe"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            >
            <label for="remember-me" class="ml-2 block text-sm text-gray-600">
              Сохранить для автоматического входа
            </label>
          </div>
          <button 
            @click="checkPassword"
            class="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
          >
            Войти
          </button>
          <p v-if="passwordError" class="text-red-500 text-sm text-center">{{ passwordError }}</p>
        </div>
      </div>
    </div>

    <!-- Main Content (hidden until password is correct) -->
    <div v-show="isPasswordCorrect" class="w-full py-4 lg:grid lg:grid-cols-3 lg:gap-[30px]">
      <!-- Left side with exam selection and results (takes 2 columns on large screens) -->
      <div class="lg:col-span-2">
        <!-- Header -->
        <header class="header-section">
          <h1 class="text-[27.5px] sm:text-[47.5px] font-bold text-color-gray-900">Программы по ЕГЭ/вступительным испытаниям</h1>
          <p class="mt-2 sm:mt-4 text-[15.5px] sm:text-[23.5px] text-color-gray-600 max-w-2xl mx-auto lg:mx-0">Выберите сданные экзамены и
            укажите баллы, чтобы найти подходящие образовательные программы.</p>
        </header>

        <!-- Exam Selection Section -->
        <div class="content-card exam-selection-card">
          <h2 class="text-[20px] sm:text-[32px] font-semibold text-color-gray-900 mb-4 sm:mb-8 flex items-center">
            <div class="flex-shrink-0 w-1 h-6 bg-blue-600 rounded-full mr-3"></div>
            Ваши результаты ЕГЭ/вступительных испытаний
          </h2>

          <div class="space-y-4 sm:space-y-6">
            <!-- Fixed Russian Language Exam -->
            <div class="bg-gray-50/50 rounded-xl p-4 sm:p-5 border border-gray-100">
              <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch w-full">
                <div class="w-full sm:w-2/3">
                  <label for="exam-subject-russian" class="block text-[14px] sm:text-[16px] font-medium text-color-gray-700 mb-1.5 sm:mb-0">Экзамен</label>
                  <input id="exam-subject-russian" type="text" :value="russianExam.subject"
                    class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 transition-colors duration-150 py-2.5 px-3.5 input-ios bg-gray-100 cursor-not-allowed text-[14px] sm:text-[16px]"
                    disabled>
                </div>
                <div class="w-full sm:w-1/3">
                  <label for="exam-score-russian" class="block text-[14px] sm:text-[16px] font-medium text-color-gray-700 mb-1.5 sm:mb-0">Балл</label>
                  <input id="exam-score-russian" type="text" :value="russianExam.score" @input="(e: Event) => {
                    const input = e.target as HTMLInputElement;
                    const value = input.value.replace(/[^\d]/g, '');
                    const numValue = parseInt(value) || 0;
                    russianExam.score = Math.min(Math.max(numValue, 0), 100);
                    input.value = russianExam.score.toString();
                  }"
                    class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 transition-colors duration-150 py-2.5 px-3.5 input-ios text-[14px] sm:text-[16px]"
                    :class="{ 'input-glow': russianExam.subject && russianExam.score === 0 }"
                    placeholder="0-100">
                </div>
              </div>
            </div>

            <!-- Additional Exams -->
            <div v-for="(exam, index) in selectedExams" :key="index"
              class="bg-gray-50/50 rounded-xl p-4 sm:p-5 border border-gray-100 relative">
              <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center w-full">
                <div class="w-full sm:w-1/2">
                  <label :for="`exam-subject-${index}`"
                    class="block text-[14px] sm:text-[16px] font-medium text-color-gray-700 mb-1.5 sm:mb-0">Экзамен</label>
                  <select :id="`exam-subject-${index}`" v-model="exam.subject"
                    class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 transition-colors duration-150 py-2.5 px-3.5 select-ios text-[14px] sm:text-[16px]"
                    :class="{'!border !border-blue-400 !bg-blue-50/30 !shadow-[0_0_8px_rgba(30,58,138,0.2)] animate-[subtle-pulse_1.5s_ease-in-out_infinite]': !exam.subject}"
                    @change="updateAvailableExams(index)">
                    <option value="">Выберите экзамен</option>
                    <option v-for="subject_option in availableSubjects[index]" :key="subject_option"
                      :value="subject_option">
                      {{ getSubjectDisplayName(subject_option) }}
                    </option>
                  </select>
                </div>
                <div class="w-full sm:w-1/2">
                  <label :for="`exam-score-${index}`" class="block text-[14px] sm:text-[16px] font-medium text-color-gray-700 mb-1.5 sm:mb-0">Балл</label>
                  <input :id="`exam-score-${index}`" type="text" :value="exam.score" @input="(e: Event) => {
                    const input = e.target as HTMLInputElement;
                    const value = input.value.replace(/[^\d]/g, '');
                    const numValue = parseInt(value) || 0;
                    exam.score = Math.min(Math.max(numValue, 0), 100);
                    input.value = exam.score.toString();
                  }"
                    class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 transition-colors duration-150 py-2.5 px-3.5 input-ios text-[14px] sm:text-[16px]"
                    :class="{ 'input-glow': !exam.subject || (exam.subject && exam.score === 0) }"
                    placeholder="0-100">
                </div>
                <div class="flex-none flex items-center justify-center sm:ml-2" style="margin-top: 9px !important;">
                  <button @click="removeExam(index)"
                    class="p-2 text-gray-400 hover:text-red-500 transition-colors duration-150 rounded-full hover:bg-gray-100 active:bg-gray-200"
                    aria-label="Удалить экзамен">
                    <svg class="w-[30px] h-[30px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <button @click="addExam"
              class="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 border border-transparent text-[14px] sm:text-[16px] font-medium rounded-lg text-color-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150">
              <svg class="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                  clip-rule="evenodd" />
              </svg>
              Добавить экзамен
            </button>
          </div>
        </div>

        <!-- Debug Log -->
        <template v-if="!isLoading">
          {{ console.log('[DEBUG] Values for prompt:', {
            isLoading, russianExamIsValid, numberOfValidAdditionalExams,
          totalValidExamsCount }) }}
        </template>

        <!-- NEW: Prompt to add more exams -->
        <div v-if="!isLoading && totalValidExamsCount < 2"
          class="bg-blue-50 rounded-2xl p-4 sm:p-6 shadow-lg border border-blue-200/80">
          <div class="flex items-center gap-4">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <svg class="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div class="flex-1">
              <h3 class="text-lg sm:text-xl font-semibold text-blue-900 mb-1 sm:mb-2">
                {{ totalValidExamsCount === 0 ? 'Укажите ваши баллы ЕГЭ/вступительных испытаний' : 'Нужен еще один экзамен' }}
              </h3>
              <p class="text-sm sm:text-base text-blue-700">
                <span v-if="totalValidExamsCount === 0">
                  Пожалуйста, укажите баллы как минимум для двух экзаменов,
                  чтобы мы могли подобрать подходящие программы.
                </span>
                <span v-else-if="totalValidExamsCount === 1">
                  Пожалуйста, укажите баллы еще как минимум для одного экзамена,
                  чтобы мы могли подобрать подходящие программы.
                </span>
              </p>
            </div>
          </div>
        </div>

        <!-- Results Section (Modified v-if) -->
        <div v-else-if="!isLoading && totalValidExamsCount >= 2 && matchingPrograms.length > 0" class="mb-6 sm:mb-12 px-2 sm:px-4">
          <h2 class="text-[20px] sm:text-[32px] font-semibold text-color-gray-900 mb-4 sm:mb-8 flex items-center">
            <div class="flex-shrink-0 w-1 h-6 bg-blue-600 rounded-full mr-3"></div>
            Подходящие программы
          </h2>

          <!-- Campus Selection - Dropdown on Mobile, Segments on Desktop -->
          <div class="mb-4 sm:mb-6">
            <!-- Mobile Dropdown -->
            <div class="block sm:hidden">
              <div class="relative">
                <button 
                  @click="isDropdownOpen = !isDropdownOpen"
                  class="campus-dropdown w-full px-4 py-3 bg-gray-50/90 backdrop-blur-xl border border-gray-200 rounded-2xl text-gray-900 text-base font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-[0_1px_2px_rgba(0,0,0,0.04),0_1px_2px_rgba(0,0,0,0.06)] flex items-center justify-between transition-all duration-200 hover:bg-gray-100/90"
                >
                  <span>{{ selectedCampus }}</span>
                  <svg 
                    class="w-5 h-5 text-gray-500 transition-transform duration-200" 
                    :class="{ 'rotate-180': isDropdownOpen }"
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
                
                <!-- Dropdown Menu -->
                <div 
                  v-if="isDropdownOpen"
                  class="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.15)] overflow-hidden"
                >
                  <div class="max-h-60 overflow-y-auto">
                    <button
                      v-for="campus in campuses"
                      :key="campus"
                      @click="selectCampus(campus)"
                      class="w-full px-4 py-3 text-left text-gray-900 hover:bg-gray-50 transition-colors duration-150 flex items-center border-b border-gray-100 last:border-b-0"
                      :class="{ 'bg-blue-50 text-blue-600 font-medium': selectedCampus === campus }"
                    >
                      <span class="flex-1">{{ campus }}</span>
                      <svg 
                        v-if="selectedCampus === campus"
                        class="w-5 h-5 text-blue-600 ml-2" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Desktop Segments -->
            <div class="hidden sm:block">
              <div class="flex space-x-2 overflow-x-auto pb-2">
                <button
                  v-for="campus in campuses"
                  :key="campus"
                  @click="selectedCampus = campus"
                  :class="[
                    'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap',
                    selectedCampus === campus 
                      ? 'bg-blue-600 text-white shadow-md' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  ]"
                >
                  {{ campus }}
                </button>
              </div>
            </div>
          </div>

          <!-- No Programs for Selected Campus Message -->
          <div v-if="filteredPrograms.length === 0" class="bg-white/80 backdrop-blur-xl rounded-2xl shadow-[0_1px_2px_rgba(0,0,0,0.04),0_1px_2px_rgba(0,0,0,0.06)] border border-black/[0.05] overflow-hidden p-6 text-center">
            <div class="inline-flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-full mb-5">
              <svg class="h-6 w-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">Программы не найдены</h3>
            <p class="text-gray-600 max-w-2xl mx-auto">
              {{ selectedCampus === 'Все кампусы' 
                ? 'По выбранным экзаменам и баллам не найдено подходящих программ. Попробуйте изменить баллы или выбрать другие экзамены.'
                : `По выбранным экзаменам и баллам не найдено подходящих программ в кампусе "${selectedCampus}". Попробуйте изменить баллы, выбрать другие экзамены или посмотреть программы в других кампусах.` }}
            </p>
          </div>

          <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 px-0 sm:px-1">
            <div v-for="program in filteredPrograms" :key="program.N"
              class="group relative bg-white rounded-xl shadow-sm hover:shadow-xl border border-gray-200 transition-all duration-300 overflow-hidden"
              @click="openProgramDetails(program)">
              <div
                class="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-blue-600/0 to-blue-600/0 group-hover:from-blue-600/5 group-hover:via-blue-600/5 group-hover:to-blue-600/5 transition-all duration-500">
              </div>
              <div class="relative p-6 space-y-3">
                <h3 class="text-size-lg font-semibold text-color-gray-900 group-hover:text-color-blue-600 break-words">{{ program.OP }}</h3>
                <p class="text-size-sm text-color-gray-500">{{ program.NP }}</p>
                <div class="text-size-xs text-color-gray-500 flex flex-wrap gap-2 items-center">
                  <span
                    class="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 pl-4 pr-4 text-size-xs font-medium text-color-gray-700 ring-1 ring-inset ring-gray-500/10">
                    Кампус: {{ program.Campus }}
                  </span>
                  <span
                    class="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 pl-4 pr-4 text-size-xs font-medium text-color-blue-700 ring-1 ring-inset ring-blue-700/10">
                    Бюджетных мест: {{ program.Places_B }}
                  </span>
                </div>
                <div>
                  <h4 class="text-xs font-medium text-gray-700 uppercase tracking-wider mb-1.5 mt-3">Требуемые экзамены:</h4>
                  <ul class="space-y-1" style="margin-left: 2px;">
                    <li v-for="(subject, s_index) in getProgramSubjects(program)" :key="s_index"
                      class="text-xs text-gray-600 flex items-center">
                      <div class="w-2 h-2 bg-blue-500 rounded-full mr-1.5 flex-shrink-0"></div>
                      <span class="font-medium" v-html="subject"></span>
                    </li>
                  </ul>
                </div>
                <div class="mt-4 pt-3 border-t border-gray-200/60 flex justify-end">
                  <button
                    class="text-sm text-blue-600 font-medium py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors duration-150">
                    Подробнее
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- No Results Message (Modified v-else-if) -->
        <div v-else-if="!isLoading && totalValidExamsCount >= 2 && matchingPrograms.length === 0"
          class="bg-white/80 backdrop-blur-xl rounded-2xl shadow-[0_1px_2px_rgba(0,0,0,0.04),0_1px_2px_rgba(0,0,0,0.06)] border border-black/[0.05] overflow-hidden p-6 text-center">
          <div class="inline-flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-full mb-5">
            <svg class="h-6 w-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">Программы не найдены</h3>
          <p class="text-gray-600 max-w-2xl mx-auto">
            По выбранным экзаменам и баллам не найдено подходящих программ.<br/>Попробуйте изменить баллы или выбрать другие экзамены.
          </p>
        </div>

        <!-- Program Details Modal -->


        <!-- Debug Section -->
        <div v-if="debugMode" class="mt-12 bg-gray-800 rounded-2xl p-6 shadow-2xl">
          <h2 class="text-xl font-semibold text-gray-100 mb-4">Debug Information</h2>
          <pre class="text-sm text-gray-300 overflow-auto max-h-96 bg-gray-900/50 p-4 rounded-lg">{{ debugInfo }}</pre>
        </div>
      </div>

      <!-- Right side with info panels (takes 1 column on large screens) -->
      <div class="mt-6 lg:mt-0 space-y-4 sm:space-y-6">
        <!-- Info Panel -->
        <div class="lg:sticky lg:top-4">
          <div class="content-card info-panel-card">
            <div class="flex items-center gap-3 mb-4">
              <div class="p-2 bg-blue-50 rounded-lg">
                <svg class="w-5 h-5 text-color-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 class="text-size-lg font-semibold text-color-gray-900">Важная информация</h3>
            </div>
            <div class="space-y-4 text-size-sm text-color-gray-600">
              <p>
                Этот инструмент поможет вам сориентироваться в программах на основе ваших результатов ЕГЭ/вступительных испытаний.
                Убедитесь, что вы указали корректные баллы.
              </p>
              <p>
                Укажите предметы, которые вы сдали, и полученные баллы. Система автоматически покажет список программ, на которые вы можете подать заявление, исходя из соответствия ваших экзаменов требованиям программ и минимальным проходным баллам.
              </p>
              <div class="border-t border-gray-200 pt-4">
                <a href="https://ba.hse.ru/docs" target="_blank"
                  class="inline-flex items-center gap-2 text-blue-600 hover:underline">
                  <span>Подробнее о программах и их вступительных испытаниях</span>
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- "А вы знали?" Panel -->
        <div class="lg:sticky lg:top-[calc(64px+220px)]">
          <div class="content-card fun-facts-card">
            <div class="relative -mt-19 -mb-4">
              <img src="@/assets/voronahse_wondering.png" alt="Воронка думает" class="w-20 h-20 object-contain -ml-2" />
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-4">А вы знали?</h3>
            <p class="text-sm text-gray-600 mb-4">{{ currentFact }}</p>
            <button @click="changeRandomFact" class="text-sm text-blue-600 hover:underline">
              А ещё?
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <ion-modal :is-open="isOpenModal" @didDismiss="handleModalDismiss" class="program-details-modal"
    :breakpoints="[0, 1]" :initial-breakpoint="1" mode="ios">
    <ion-header class="ion-no-border">
      <ion-toolbar>
        <ion-title class="text-lg font-semibold">{{ selectedProgram?.OP }}</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="handleModalDismiss" class="text-gray-500">
            <ion-icon :icon="close" slot="icon-only"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding">
      <div class="space-y-6">
        <p class="text-gray-600 text-sm leading-relaxed" style="display: none;">{{ selectedProgram?.NP }}</p>

        <div class="space-y-6">
          <p class="text-gray-600 text-sm leading-relaxed">{{ selectedProgram?.NP }}</p>

          <div class="grid grid-cols-2 gap-3" style="display: grid !important; grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 12px !important;">
            <div class="bg-gradient-to-br from-blue-50 to-blue-50/50 rounded-2xl p-3.5 backdrop-blur-sm" style="border-radius: 15px !important; grid-column: 1 !important;">
              <div class="text-xs font-medium text-blue-600 mb-1">Бюджетные места</div>
              <div class="text-base font-semibold text-gray-900">{{ selectedProgram?.Places_B }}</div>
            </div>
            <div v-if="selectedProgram?.pass_score_B" class="bg-gradient-to-br from-blue-50 to-blue-50/50 rounded-2xl p-3.5 backdrop-blur-sm" style="border-radius: 15px !important; grid-column: 2 !important;">
              <div class="text-xs font-medium text-blue-600 mb-1">Проходной балл в 2025 году</div>
              <div class="text-base font-semibold text-gray-900">{{ selectedProgram.pass_score_B }}</div>
            </div>
            <div class="bg-gradient-to-br from-blue-50 to-blue-50/50 rounded-2xl p-3.5 backdrop-blur-sm" style="border-radius: 15px !important; grid-column: 1 !important;">
              <div class="text-xs font-medium text-blue-600 mb-1">Места в рамках особой квоты</div>
              <div class="text-base font-semibold text-gray-900">{{ selectedProgram?.PLACES_B_OSOB }}</div>
            </div>
            <div class="bg-gradient-to-br from-blue-50 to-blue-50/50 rounded-2xl p-3.5 backdrop-blur-sm" style="border-radius: 15px !important; grid-column: 2 !important;">
              <div class="text-xs font-medium text-blue-600 mb-1">Места в рамках целевой квоты</div>
              <div class="text-base font-semibold text-gray-900">{{ selectedProgram?.PLACES_B_TSEL }}</div>
            </div>
            <div class="bg-gradient-to-br from-blue-50 to-blue-50/50 rounded-2xl p-3.5 backdrop-blur-sm" style="border-radius: 15px !important; grid-column: 1 !important;">
              <div class="text-xs font-medium text-blue-600 mb-1">Места в рамках отдельной квоты</div>
              <div class="text-base font-semibold text-gray-900">{{ selectedProgram?.PLACES_B_OTD }}</div>
            </div>
            <div class="bg-gradient-to-br from-blue-50 to-blue-50/50 rounded-2xl p-3.5 backdrop-blur-sm" style="border-radius: 15px !important; grid-column: 2 !important;">
              <div class="text-xs font-medium text-blue-600 mb-1">Коммерческие места</div>
              <div class="text-base font-semibold text-gray-900">{{ selectedProgram?.PLACES_K }}</div>
            </div>
            <div class="bg-gradient-to-br from-violet-50 to-violet-50/50 rounded-2xl p-3.5 backdrop-blur-sm" style="border-radius: 15px !important; grid-column: 1 / span 2 !important;">
              <div class="text-xs font-medium text-violet-600 mb-1">Стоимость обучения</div>
              <div class="text-base font-semibold text-success-700">{{ selectedProgram?.PRICE }} тыс. ₽/год</div>
            </div>
          </div>
        </div>

        <div v-if="selectedProgram?.pass_score_B && selectedProgram.pass_score_B.includes('-*')" class="text-xs text-gray-500 mt-2">
          *На эту программу все бюджетные места были заняты поступившими в приоритетном порядке (особая, целевая, отдельная квоты, поступающими по олимпиадам школьников).
        </div>

        <div class="space-y-4">
          <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wider">Требуемые экзамены</h4>
          <div class="space-y-3">
            <div v-for="(subject, s_index) in getProgramSubjects(selectedProgram)" :key="s_index"
              class="bg-gradient-to-br from-gray-50 to-gray-50/50 rounded-2xl p-3.5 backdrop-blur-sm">
              <div class="text-sm font-medium text-gray-900 mb-1" v-html="subject"></div>
            </div>
          </div>
        </div>

        <div v-if="selectedProgram?.website_link" class="mt-6 flex justify-center">
          <a :href="selectedProgram.website_link" target="_blank" 
            class="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors duration-150 shadow-sm hover:shadow-md"
            style="border-radius: 25px !important; border: 1px solid rgba(255, 255, 255, 0.2) !important;">
            <span>Узнать больше о программе</span>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
    </ion-content>
  </ion-modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Papa from 'papaparse';
import { ParseResult } from 'papaparse';
import { modalController } from '@ionic/vue';
import { close } from 'ionicons/icons';

import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonModal,
  IonButtons,
  IonButton,
  IonIcon,
} from "@ionic/vue";

interface Exam {
  subject: string;
  score: number;
}

interface Program {
  N: string;
  Campus: string;
  NP: string;
  OP: string;
  VI_1: string;
  VI_1_SCORE: string;
  VI_2: string;
  VI_2_SCORE: string;
  VI_3: string;
  VI_3_SCORE: string;
  VI_4: string;
  VI_4_SCORE: string;
  Places_B: string;
  PLACES_B_OSOB: string;
  PLACES_B_TSEL: string;
  PLACES_B_OTD: string;
  PLACES_K: string;
  PRICE: string;
  pass_score_B: string;
  website_link: string;
  [key: string]: string;
}

export default defineComponent({
  name: 'ProgramsByExamsView',

  props: {
    domain: {
      type: String,
      required: true
    },
    storageUri: {
      type: String,
      required: true,
      default: ""
    },
    isApp: {
      type: Boolean,
      default: false
    }
  },

  components: {
    IonContent,
    IonHeader,
    IonPage,
    IonTitle,
    IonToolbar,
    IonModal,
    IonButtons,
    IonButton,
    IonIcon,
  },

  data() {
    const allSubjects = [
      'биология',
      'география',
      'иностранный язык',
      'информатика',
      'история',
      'литература',
      'математика',
      'обществознание',
      'русский язык',
      'физика',
      'химия'
    ];

    return {
      isOpenModal: false,
      isLoading: true,
      programs: [] as Program[],
      russianExam: { subject: 'русский язык', score: 0 } as Exam,
      selectedExams: [] as Exam[],
      availableSubjects: [] as string[][],
      debugMode: false,
      selectedProgram: null as Program | null,
      allSubjects,
      selectedCampus: 'Все кампусы',
      campuses: ['Все кампусы','Москва', 'Пермь', 'Нижний Новгород', 'Санкт-Петербург'  ],
      funFacts: [
        "НИУ ВШЭ предлагает более 100 бакалаврских программ.",
        "Студенты ВШЭ могут выбрать индивидуальную образовательную траекторию.",
        "ВШЭ имеет 4 кампуса: в Москве, Санкт-Петербурге, Нижнем Новгороде и Перми.",
        "Вышка регулярно входит в международные рейтинги лучших университетов.",
        "В НИУ ВШЭ активно развивается проектное обучение."
      ],
      currentFact: "",
      close,
      isPasswordCorrect: true,
      passwordInput: '',
      passwordError: '',
      rememberMe: false,
      isDropdownOpen: false,
      resizeObserver: null as ResizeObserver | null,
    }
  },

  computed: {
    matchingPrograms(): Program[] {
      const userSubmittedExams = [
        this.russianExam,
        ...this.selectedExams
      ].filter(e => e.subject && e.score && e.score > 0);

      if (userSubmittedExams.length === 0) {
        return [];
      }

      console.log('Selected exams by user (including Russian):', JSON.stringify(userSubmittedExams, null, 2));

      return this.programs.filter(program => {
        // Debug log for each program being checked
        console.log(`\nChecking program: ${program.OP} (${program.Campus})`);
        
        // Ensure program has basic validity if not already filtered during load
        if (!program || !program.N || !program.OP) {
          console.log('  Program invalid - missing basic data');
          return false;
        }

        const programExamMinScores = this.getProgramExamScores(program);
        const programRequiredGroups = this.getRequiredSubjects(program);

        console.log('  Program required groups:', JSON.stringify(programRequiredGroups, null, 2));
        console.log('  Program min scores:', JSON.stringify(programExamMinScores, null, 2));

        if (programRequiredGroups.length === 0) {
          console.log('  No required groups found');
          return false;
        }

        // Check if we have enough exams to satisfy the program requirements
        if (userSubmittedExams.length < programRequiredGroups.length) {
          console.log('  Not enough exams submitted');
          return false;
        }

        // For each priority group, check if at least one subject in that group is satisfied
        const allProgramGroupsSatisfied = programRequiredGroups.every(group => {
          console.log('  Checking group:', group);
          // Check if any subject in this priority group is satisfied
          const groupSatisfied = group.some(requiredSubjectInGroup => {
            const userExamForThisSubject = userSubmittedExams.find(e => e.subject === requiredSubjectInGroup);
            console.log(`    Checking subject ${requiredSubjectInGroup}:`, userExamForThisSubject);

            if (!userExamForThisSubject) {
              console.log(`    No matching exam found for ${requiredSubjectInGroup}`);
              return false;
            }

            const minScoreForThisProgramSubject = programExamMinScores[requiredSubjectInGroup];
            console.log(`    Min score required: ${minScoreForThisProgramSubject}, User score: ${userExamForThisSubject.score}`);

            if (minScoreForThisProgramSubject === undefined) {
              console.log(`    Min score for ${requiredSubjectInGroup} not defined`);
              return false;
            }

            const isSatisfied = userExamForThisSubject.score >= minScoreForThisProgramSubject;
            console.log(`    Score requirement satisfied: ${isSatisfied}`);
            return isSatisfied;
          });

          console.log(`  Group satisfied: ${groupSatisfied}`);
          return groupSatisfied;
        });

        console.log(`Program ${program.OP} match result: ${allProgramGroupsSatisfied}`);
        return allProgramGroupsSatisfied;
      });
    },

    filteredPrograms(): Program[] {
      if (this.selectedCampus === 'Все кампусы') {
        return this.matchingPrograms;
      }
      return this.matchingPrograms.filter(program => program.Campus === this.selectedCampus);
    },

    debugInfo(): string {
      return JSON.stringify({
        russianExam: this.russianExam,
        selectedExams: this.selectedExams,
        availableSubjects: this.availableSubjects,
        matchingProgramsCount: this.matchingPrograms.length,
        programsCount: this.programs.length,
        russianExamIsValid: this.russianExamIsValid,
        numberOfValidAdditionalExams: this.numberOfValidAdditionalExams,
        totalValidExamsCount: this.totalValidExamsCount
      }, null, 2);
    },

    russianExamIsValid(): boolean {
      const isValid = !!this.russianExam.subject && this.russianExam.score > 0;
      console.log('[DEBUG] russianExamIsValid:', isValid, 'Score:', this.russianExam.score);
      return isValid;
    },

    numberOfValidAdditionalExams(): number {
      const count = this.selectedExams.filter(e => e.subject && e.score > 0).length;
      console.log('[DEBUG] numberOfValidAdditionalExams:', count);
      return count;
    },

    totalValidExamsCount(): number {
      const count = (this.russianExamIsValid ? 1 : 0) + this.numberOfValidAdditionalExams;
      console.log('[DEBUG] totalValidExamsCount:', count);
      return count;
    },

    // Add new computed property to check for incomplete exams
    incompleteExams(): { [key: string]: boolean } {
      const result: { [key: string]: boolean } = {};

      // Check Russian exam
      if (this.russianExam.subject && this.russianExam.score === 0) {
        result['russian'] = true;
      }

      // Check additional exams
      this.selectedExams.forEach((exam, index) => {
        if (exam.subject && exam.score === 0) {
          result[`exam-${index}`] = true;
        }
      });

      return result;
    }
  },

  methods: {
    updateParentHeight() {
      try {
        const height = document.body.scrollHeight;
        window.parent.postMessage({ height }, '*');
      } catch (error) {
        console.error('Error sending height update:', error);
      }
    },

    async loadPrograms() {
      try {
        const response = await fetch(`${this.storageUri.replace(/\/$/, '')}/BA_ProgramsByExams.csv`, { cache: 'no-store' });
        const csvText = await response.text();

        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true, // Skip empty lines in the CSV
          complete: (results: ParseResult<Program>) => {
            // Filter out programs that are essentially empty or lack crucial identifiers
            this.programs = results.data.filter((p: Program) => {
              const isValid = p && p.N && p.N.trim() !== '' && p.OP && p.OP.trim() !== '';
              return isValid;
            });
            this.isLoading = false;
            console.log('Programs loaded (after filtering invalid/empty rows):', this.programs.length);
            this.$nextTick(() => this.updateParentHeight());
          },
          error: (error: Error) => {
            console.error('Error loading programs:', error);
            this.isLoading = false;
            this.$nextTick(() => this.updateParentHeight());
          }
        });
      } catch (error) {
        console.error('Error fetching programs:', error);
        this.isLoading = false;
        this.$nextTick(() => this.updateParentHeight());
      }
    },

    addExam() {
      const newExam = { subject: '', score: 0 };
      this.selectedExams.push(newExam);
      this.updateAvailableExams(this.selectedExams.length - 1);
    },

    removeExam(index: number) {
      this.selectedExams.splice(index, 1);
      this.availableSubjects.splice(index, 1);
      this.selectedExams.forEach((_, i) => this.updateAvailableExams(i));
    },

    updateAvailableExams(index: number) {
      const currentlySelectedInOtherSlots = this.selectedExams
        .filter((_, i) => i !== index)
        .map(exam => exam.subject)
        .filter(Boolean);

      const subjectsToExclude = [this.russianExam.subject, ...currentlySelectedInOtherSlots];

      if (!this.availableSubjects[index]) {
        this.availableSubjects[index] = [];
      }

      this.availableSubjects[index] = this.allSubjects.filter(
        subject => !subjectsToExclude.includes(subject)
      );

      // Ensure score is set to 0 when a subject is selected
      if (this.selectedExams[index].subject && this.selectedExams[index].score === undefined) {
        this.selectedExams[index].score = 0;
      }
    },

    getProgramExamScores(program: Program): Record<string, number> {
      const scores: Record<string, number> = {};

      // Check each priority level
      for (let i = 1; i <= 4; i++) {
        const subjects = program[`VI_${i}`]?.split('/').map(s => s.trim()) || [];
        const minScore = parseInt(program[`VI_${i}_SCORE`] || '0');

        subjects.forEach(subject => {
          // Only add non-creative test subjects to the scores map
          if (subject && !subject.toLowerCase().includes('творческое испытание')) {
            scores[subject] = minScore;
          }
        });
      }

      return scores;
    },

    getRequiredSubjects(program: Program): string[][] {
      const requiredGroups: string[][] = [];

      // Check each priority level
      for (let i = 1; i <= 4; i++) {
        const subjects = program[`VI_${i}`]?.split('/').map(s => s.trim()) || [];
        // Only add non-empty groups
        if (subjects.length > 0 && subjects[0] !== '') {
          // Filter out creative test subjects but keep other subjects in the same group
          const filteredSubjects = subjects.filter(s => !s.toLowerCase().includes('творческое испытание'));
          if (filteredSubjects.length > 0) {
            requiredGroups.push(filteredSubjects);
          }
        }
      }

      return requiredGroups;
    },

    getSubjectDisplayName(subject: string): string {
      if (subject === 'математика') {
        return 'математика (профильная)';
      }
      return subject;
    },

    getProgramSubjects(program: Program | null): string[] {
      if (!program) return [];
      
      const subjects: string[] = [];
      
      // Process all priorities
      for (let i = 1; i <= 4; i++) {
        const subjectList = program[`VI_${i}`];
        const score = program[`VI_${i}_SCORE`];
        
        if (subjectList && score) {
          const subjectGroup = subjectList.split('/');
          if (subjectGroup.length > 0) {
            subjects.push(`${i}. ${subjectGroup.join(' или ')} <span class="text-blue-600 italic">мин. ${score} баллов</span>`);
          }
        }
      }
      
      return subjects;
    },

    handleModalDismiss() {
      this.selectedProgram = null;
      this.isOpenModal = false;
      console.log('Modal dismissed, isOpenModal:', this.isOpenModal);
    },

    openProgramDetails(program: Program) {
      this.selectedProgram = program;
      this.isOpenModal = true;
      console.log('Modal opened, isOpenModal:', this.isOpenModal);
    },

    changeRandomFact() {
      const newFact =
        this.funFacts[Math.floor(Math.random() * this.funFacts.length)];
      // Ensure we don't show the same fact twice in a row
      if (newFact === this.currentFact && this.funFacts.length > 1) {
        this.changeRandomFact();
        return;
      }
      this.currentFact = newFact;
    },

    // Add new method for handling score input
    handleScoreInput(event: Event, exam: Exam) {
      const input = event.target as HTMLInputElement;
      const value = input.value.replace(/[^ -]/g, ''); // Remove non-ASCII characters
      const numValue = parseInt(value) || 0;

      // Ensure value is between 0 and 100
      exam.score = Math.min(Math.max(numValue, 0), 100);

      // Update input value to ensure it's a valid number
      input.value = exam.score.toString();
    },

    checkPassword() {
      if (this.passwordInput === 'meowmeow') {
        this.isPasswordCorrect = true;
        this.passwordError = '';
        this.passwordInput = '';
        
        if (this.rememberMe) {
          localStorage.setItem('programsByExamsAuth', 'true');
        }
      } else {
        this.passwordError = 'Неверный пароль';
      }
    },

    checkStoredAuth() {
      const storedAuth = localStorage.getItem('programsByExamsAuth');
      if (storedAuth === 'true') {
        this.isPasswordCorrect = true;
      }
    },

    getSliderPosition(): number {
      const index = this.campuses.indexOf(this.selectedCampus);
      if (index === -1) return 0;
      
      // Get the segment button element
      const segmentButton = document.querySelector(`ion-segment-button[data-index="${index}"]`);
      if (!segmentButton) return 0;
      
      // Get the segment container
      const segment = document.querySelector('.custom-segment');
      if (!segment) return 0;
      
      // Calculate position
      const buttonRect = segmentButton.getBoundingClientRect();
      const segmentRect = segment.getBoundingClientRect();
      
      return buttonRect.left - segmentRect.left;
    },

    selectCampus(campus: string) {
      this.selectedCampus = campus;
      this.isDropdownOpen = false;
    },

    handleClickOutside(event: Event) {
      const target = event.target as HTMLElement;
      if (!target.closest('.campus-dropdown')) {
        this.isDropdownOpen = false;
      }
    },

    checkScrollShadow() {
      const container = document.querySelector('.segment-container');
      if (container) {
        const observer = new ResizeObserver(() => {
          container.classList.toggle('has-content', container.scrollWidth > container.clientWidth);
        });
        observer.observe(container);

        // Also check on scroll
        container.addEventListener('scroll', () => {
          const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
          container.classList.toggle('has-content', !isAtEnd);
        });
      }
    }
  },

  watch: {
    selectedCampus() {
      // Force a reflow to ensure smooth animation
      this.$nextTick(() => {
        const slider = document.querySelector('.segment-slider');
        if (slider) {
          slider.classList.remove('sliding');
          void (slider as HTMLElement).offsetWidth; // Force reflow
          slider.classList.add('sliding');
        }
      });
    },
    isLoading(val: boolean) {
      if (!val) {
        this.$nextTick(() => this.updateParentHeight());
      }
    },
  },

  mounted() {
    console.log('ProgramsByExamsView mounted');
    this.loadPrograms();
    this.changeRandomFact();
    this.checkStoredAuth(); // Check for stored auth on mount
    this.checkScrollShadow();
    
    // Add click outside handler for dropdown
    document.addEventListener('click', this.handleClickOutside);

    this.$nextTick(() => this.updateParentHeight());

    const resizeObserver = new ResizeObserver(() => {
      this.updateParentHeight();
    });
    if (this.$el) {
      resizeObserver.observe(this.$el as Element);
      this.resizeObserver = resizeObserver;
    }
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
});
</script>

<style scoped>
.container {
  width: 100% !important;
  max-width: 100% !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

/* Add any very specific styles here if Tailwind isn't enough, but try to keep it minimal */
/* For example, custom scrollbar styling if needed */
::-webkit-scrollbar {
  width: 8px !important;
  height: 8px !important;
}

::-webkit-scrollbar-track {
  background: transparent !important;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1 !important;
  /* Tailwind gray-400 */
  border-radius: 10px !important;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8 !important;
  /* Tailwind gray-500 */
}

/* Ensure the overall background matches AchievementsViewerWidget if needed */
.min-h-screen {
  background: white !important;
  /* or the specific background from AchievementsViewerWidget */
  overflow-x: hidden !important; /* Prevent page overflow */
}

/* iOS-style select to match AchievementsViewerWidget */
.select-ios {
  -webkit-appearance: none !important;
  appearance: none !important;
  background-color: rgba(243, 244, 246, 0.8) !important;
  /* bg-gray-100/80 */
  color: rgb(17, 24, 39) !important;
  /* text-gray-900 */
  border-radius: 10px !important;
  border: 1px solid rgba(209, 213, 219, 0.7) !important;
  /* border-gray-300/70 */
  padding: 10.4px 44px 10.4px 16px !important;
  /* Adjusted padding */
  font-size: 16px !important;
  /* Adjusted font size */
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' viewBox='0 0 24 24' stroke='rgb(107, 114, 128)' stroke-width='2'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3e%3c/svg%3e") !important;
  /* gray-500 icon */
  background-repeat: no-repeat !important;
  background-position: right 16px center !important;
  background-size: 16px !important;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
}

.select-ios:focus {
  border-color: rgb(59, 130, 246) !important;
  /* focus:border-blue-500 */
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2) !important;
  /* focus:ring-2 focus:ring-blue-200 */
  outline: none !important;
}

/* iOS-style input to match AchievementsViewerWidget */
.input-ios {
  background-color: rgba(243, 244, 246, 0.8) !important;
  /* bg-gray-100/80 */
  color: rgb(17, 24, 39) !important;
  /* text-gray-900 */
  border-radius: 10px !important;
  border: 1px solid rgba(209, 213, 219, 0.7) !important;
  /* border-gray-300/70 */
  padding: 10.4px 16px !important;
  /* Adjusted padding */
  font-size: 16px !important;
  /* Adjusted font size */
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
}

.input-ios:focus {
  border-color: rgb(59, 130, 246) !important;
  /* focus:border-blue-500 */
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2) !important;
  /* focus:ring-2 focus:ring-blue-200 */
  outline: none !important;
}

.input-ios::placeholder {
  color: rgb(107, 114, 128) !important;
  /* placeholder-gray-500 */
  opacity: 1 !important;
}

/* Custom subtle pulse animation */
@keyframes subtle-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 8px rgba(30, 58, 138, 0.2);
  }

  50% {
    opacity: 0.98;
    transform: scale(1.005);
    box-shadow: 0 0 10px rgba(30, 58, 138, 0.25);
  }
}

/* Override Tailwind's animate-pulse */
.animate-\[subtle-pulse_1\.5s_ease-in-out_infinite\] {
  animation: subtle-pulse 1.5s ease-in-out infinite;
}

/* Updated Ionic Modal Styles */
:deep(.program-details-modal) {
  --height: auto;
  --max-height: 90vh;
  --border-radius: 16px 16px 0 0 !important;
  --box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
}

:deep(.program-details-modal::part(content)) {
  padding: 0;
}

:deep(.program-details-modal ion-header) {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

:deep(.program-details-modal ion-content) {
  --padding-top: 20px !important;
  --padding-bottom: 20px !important;
  --padding-start: 20px !important;
  --padding-end: 20px !important;
}

:deep(.program-details-modal ion-title) {
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #1e293b !important;
}

:deep(.program-details-modal ion-toolbar) {
  --background: transparent !important;
  --border-color: transparent !important;
}

:deep(.program-details-modal ion-button) {
  --padding-start: 8px !important;
  --padding-end: 8px !important;
  --padding-top: 8px !important;
  --padding-bottom: 8px !important;
  --color: #64748b !important;
}

:deep(.program-details-modal ion-button:hover) {
  --color: #1e293b !important;
}

:deep(.program-details-modal ion-icon) {
  font-size: 24px !important;
}

/* Updated Apple-style Segment Styles with Slider */
:deep(.custom-segment) {
  --background: rgba(235, 235, 245, 0.8) !important;
  --background-checked: white !important;
  --color: rgb(60, 60, 67, 0.6) !important;
  --color-checked: rgb(0, 122, 255) !important;
  --indicator-color: transparent !important;
  --border-radius: 6px !important;
  --padding-top: 4px !important;
  --padding-bottom: 4px !important;
  --padding-start: 4px !important;
  --padding-end: 4px !important;
  --indicator-box-shadow: none !important;
  background: rgba(235, 235, 245, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  -webkit-backdrop-filter: blur(10px) !important;
  border-radius: 6px !important;
  padding: 4px !important;
  box-shadow: 0 0.5px 1px rgba(0, 0, 0, 0.1) !important;
  position: relative !important;
  overflow: visible !important;
  display: inline-flex !important;
  min-width: min-content !important;
  width: auto !important;
}

.segment-slider {
  position: absolute !important;
  top: 4px !important;
  left: 4px !important;
  height: calc(100% - 8px) !important;
  background: white !important;
  border-radius: 4px !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  pointer-events: none !important;
  z-index: 1 !important;
}

:deep(.custom-segment ion-segment-button) {
  --padding-top: 8px !important;
  --padding-bottom: 8px !important;
  --padding-start: 12px !important;
  --padding-end: 12px !important;
  --indicator-height: 0 !important;
  --color-checked: rgb(0, 122, 255) !important;
  --background-checked: transparent !important;
  --background-hover: rgba(60, 60, 67, 0.1) !important;
  --background-hover-opacity: 1 !important;
  --ripple-color: rgb(0, 122, 255) !important;
  --transition: 200ms cubic-bezier(0.4, 0, 0.2, 1) !important;
  border-radius: 4px !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: -0.01em !important;
  margin: 0 2px !important;
  min-width: 80px !important;
  position: relative !important;
  z-index: 2 !important;
  white-space: nowrap !important;
}

:deep(.custom-segment ion-segment-button::part(indicator)) {
  display: none !important;
}

:deep(.custom-segment ion-segment-button::part(indicator-background)) {
  display: none !important;
}

:deep(.custom-segment ion-segment-button.ion-segment-button-checked) {
  color: rgb(0, 122, 255) !important;
  font-weight: 600 !important;
  background: transparent !important;
}

:deep(.custom-segment ion-segment-button:not(.ion-segment-button-checked):hover) {
  background: rgba(60, 60, 67, 0.1) !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  :deep(.custom-segment) {
    --border-radius: 5px !important;
    border-radius: 5px !important;
  }

  .segment-slider {
    border-radius: 3px !important;
  }

  :deep(.custom-segment ion-segment-button) {
    border-radius: 3px !important;
  }
}

/* Custom text size classes with balanced proportions */
.text-size-xs {
  font-size: 16px !important;
  line-height: 1.5 !important;
  letter-spacing: -0.01em !important;
}

.text-size-sm {
  font-size: 16px !important;
  line-height: 1.1 !important;
  letter-spacing: -0.01em !important;
}

.text-size-base {
  font-size: 20px !important;
  line-height: 1.6 !important;
  letter-spacing: -0.01em !important;
}

.text-size-lg {
  font-size: 24px !important;
  line-height: 1.4 !important;
  letter-spacing: -0.02em !important;
}

.text-size-xl {
  font-size: 28px !important;
  line-height: 1.3 !important;
  letter-spacing: -0.02em !important;
}

.text-size-2xl {
  font-size: 32px !important;
  line-height: 1.3 !important;
  letter-spacing: -0.02em !important;
}

.text-size-3xl {
  font-size: 40px !important;
  line-height: 1.2 !important;
  letter-spacing: -0.03em !important;
}

.text-size-4xl {
  font-size: 48px !important;
  line-height: 1.1 !important;
  letter-spacing: -0.03em !important;
}

.text-size-5xl {
  font-size: 56px !important;
  line-height: 1.1 !important;
  letter-spacing: -0.03em !important;
}

/* Add more specific selectors for text elements */
.min-h-screen h1 {
  font-size: 48px !important;
  line-height: 1.1 !important;
  letter-spacing: -0.03em !important;
  font-weight: 700 !important;
}

.min-h-screen h2 {
  font-size: 32px !important;
  line-height: 1.3 !important;
  letter-spacing: -0.02em !important;
  font-weight: 600 !important;
}

.min-h-screen h3 {
  font-size: 24px !important;
  line-height: 1.4 !important;
  letter-spacing: -0.02em !important;
  font-weight: 600 !important;
}

.min-h-screen p {
  font-size: 16px !important;
  line-height: 1.6 !important;
  letter-spacing: -0.01em !important;
}

.min-h-screen label {
  font-size: 16px !important;
  line-height: 1.5 !important;
  letter-spacing: -0.01em !important;
  font-weight: 500 !important;
}

.min-h-screen button {
  font-size: 16px !important;
  line-height: 1.5 !important;
  letter-spacing: -0.01em !important;
  font-weight: 500 !important;
}

/* Add specific styles for input and select elements */
.min-h-screen input,
.min-h-screen select {
  font-size: 16px !important;
  line-height: 1.5 !important;
  letter-spacing: -0.01em !important;
}

/* Add specific styles for modal content */
:deep(.program-details-modal) h3 {
  font-size: 24px !important;
  line-height: 1.4 !important;
  letter-spacing: -0.02em !important;
  font-weight: 600 !important;
}

:deep(.program-details-modal) p {
  font-size: 18px !important;
  line-height: 1.6 !important;
  letter-spacing: -0.01em !important;
}

:deep(.program-details-modal) .text-xs {
  font-size: 14px !important;
  line-height: 1.5 !important;
  letter-spacing: -0.01em !important;
}

:deep(.program-details-modal) .text-sm {
  font-size: 16px !important;
  line-height: 1.5 !important;
  letter-spacing: -0.01em !important;
}

:deep(.program-details-modal) .text-base {
  font-size: 18px !important;
  line-height: 1.6 !important;
  letter-spacing: -0.01em !important;
}

/* Add specific styles for segment buttons */
:deep(.custom-segment ion-segment-button) {
  font-size: 16px !important;
  line-height: 1.5 !important;
  letter-spacing: -0.01em !important;
  font-weight: 500 !important;
}

:deep(.custom-segment ion-segment-button.ion-segment-button-checked) {
  font-weight: 600 !important;
}

/* Add spacing utilities for better vertical rhythm */
.space-y-6 > :not([hidden]) ~ :not([hidden]) {
  margin-top: 24px !important;
}

.space-y-8 > :not([hidden]) ~ :not([hidden]) {
  margin-top: 32px !important;
}

.space-y-10 > :not([hidden]) ~ :not([hidden]) {
  margin-top: 40px !important;
}

/* Adjust padding for better text containers */
.p-6 {
  padding: 24px !important;
}

.p-8 {
  padding: 32px !important;
}

/* Adjust margins for better spacing */
.mb-6 {
  margin-bottom: 24px !important;
}

.mb-8 {
  margin-bottom: 32px !important;
}

.mb-10 {
  margin-bottom: 40px !important;
}

/* Adjust input and select heights for better proportions */
.input-ios, .select-ios {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  min-height: 48px !important;
}

/* Adjust button padding for better proportions */
button {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

/* Adjust card spacing */
.rounded-2xl {
  border-radius: 16px !important;
}

/* Adjust grid gap for better spacing */
.gap-4 {
  gap: 20px !important;
}

/* Adjust icon sizes proportionally */
.w-5 {
  width: 24px !important;
}

.h-5 {
  height: 24px !important;
}

.w-6 {
  width: 28px !important;
}

.h-6 {
  height: 28px !important;
}

/* Custom text color classes */
.text-color-gray-400 {
  color: rgb(156 163 175) !important;
}

.text-color-gray-500 {
  color: rgb(107 114 128) !important;
}

.text-color-gray-600 {
  color: rgb(75 85 99) !important;
}

.text-color-gray-700 {
  color: rgb(55 65 81) !important;
}

.text-color-gray-800 {
  color: rgb(31 41 55) !important;
}

.text-color-gray-900 {
  color: rgb(17 24 39) !important;
}

.text-color-blue-500 {
  color: rgb(59 130 246) !important;
}

.text-color-blue-600 {
  color: rgb(37 99 235) !important;
}

.text-color-blue-700 {
  color: rgb(29 78 216) !important;
}

.text-color-white {
  color: rgb(255 255 255) !important;
}

/* Input glow effect */
.input-glow {
  border: 1px solid rgb(96 165 250) !important; /* blue-400 */
  background-color: rgba(219 234 254, 0.3) !important; /* blue-50/30 */
  box-shadow: 0 0 8px rgba(30, 58, 138, 0.2) !important;
  animation: subtle-pulse 1.5s ease-in-out infinite !important;
}

@keyframes subtle-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 8px rgba(30, 58, 138, 0.2);
  }
  50% {
    opacity: 0.98;
    transform: scale(1.005);
    box-shadow: 0 0 10px rgba(30, 58, 138, 0.25);
  }
}

/* Mobile-specific adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 28px !important;
    line-height: 1.2 !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
    line-height: 1.3 !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen p {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  .min-h-screen label {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen button {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen input,
  .min-h-screen select {
    font-size: 14px !important;
    line-height: 1.4 !important;
    padding: 8px !important;
  }

  /* Adjust card spacing and padding */
  .rounded-xl {
    border-radius: 12px !important;
  }

  .p-4 {
    padding: 16px !important;
  }

  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 16px !important;
  }

  /* Adjust grid gap */
  .gap-3 {
    gap: 12px !important;
  }

  /* Adjust icon sizes */
  .w-5 {
    width: 20px !important;
  }

  .h-5 {
    height: 20px !important;
  }

  .w-6 {
    width: 24px !important;
  }

  .h-6 {
    height: 24px !important;
  }
}

/* Adjust modal styles for mobile */
@media (max-width: 640px) {
  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }

  :deep(.program-details-modal ion-header) {
    padding: 12px;
  }

  :deep(.program-details-modal ion-content) {
    --padding-top: 16px;
    --padding-bottom: 16px;
    --padding-start: 16px;
    --padding-end: 16px;
  }

  :deep(.program-details-modal h3) {
    font-size: 19px !important;
  }

  :deep(.program-details-modal p) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-xs) {
    font-size: 13px !important;
  }

  :deep(.program-details-modal .text-sm) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-base) {
    font-size: 17px !important;
  }
}

/* Adjust segment styles for mobile */
@media (max-width: 640px) {
  :deep(.custom-segment) {
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 4px;
    --padding-end: 4px;
  }

  :deep(.custom-segment ion-segment-button) {
    font-size: 14px !important;
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 8px;
    --padding-end: 8px;
  }
}

/* Mobile-specific input styles */
@media (max-width: 640px) {
  .input-ios,
  .select-ios {
    padding: 10px 14px !important;
    min-height: 44px !important;
    font-size: 14px !important;
  }

  .input-ios::placeholder {
    font-size: 14px !important;
  }

  .select-ios {
    background-position: right 12px center !important;
    background-size: 14px !important;
    padding-right: 36px !important;
  }

  /* Adjust label spacing */
  label {
    margin-bottom: 6px !important;
  }

  /* Make buttons more touch-friendly */
  button {
    min-height: 44px !important;
    padding: 10px 16px !important;
  }

  /* Adjust spacing between exam elements */
  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 12px !important;
  }

  .gap-3 {
    gap: 12px !important;
  }
}

/* Base border radius styles */
.min-h-screen .rounded-lg {
  border-radius: 8px !important;
}

.min-h-screen .rounded-xl {
  border-radius: 12px !important;
}

.min-h-screen .rounded-2xl {
  border-radius: 16px !important;
}

/* Input and select specific styles */
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  border-radius: 8px !important;
}

/* Button specific styles */
.min-h-screen button {
  border-radius: 8px !important;
}

/* Card specific styles */
.min-h-screen .bg-white {
  border-radius: 16px !important;
}

/* Modal specific styles */
:deep(.program-details-modal) {
  --border-radius: 16px 16px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 16px !important;
}

/* Segment specific styles */
:deep(.custom-segment) {
  --border-radius: 8px !important;
}

:deep(.custom-segment ion-segment-button) {
  border-radius: 8px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl {
    border-radius: 12px !important;
  }

  .min-h-screen .rounded-2xl {
    border-radius: 14px !important;
  }

  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }
}

/* Exam entry styles */
.bg-gray-50\/50 {
  background-color: rgba(249, 250, 251, 0.5) !important;
  backdrop-filter: blur(8px) !important;
}

.border-gray-100 {
  border-color: rgba(243, 244, 246, 0.8) !important;
}

/* Add hover effect to exam entries */
.bg-gray-50\/50:hover {
  background-color: rgba(249, 250, 251, 0.7) !important;
  border-color: rgba(229, 231, 235, 0.8) !important;
  transition: all 0.2s ease-in-out;
}

/* Compact exam entry layout */
@media (min-width: 640px) {
  .exam-entry-row {
    flex-direction: row !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 0 !important;
  }
}
@media (max-width: 639px) {
  .exam-entry-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 8px !important;
  }
}

/* Special border radius class with !important */
.force-radius {
  border-radius: 17px !important;
}

.force-radius-sm {
  border-radius: 12px !important;
}

/* Apply to all elements that need border radius */
.min-h-screen .rounded-lg,
.min-h-screen .rounded-xl,
.min-h-screen .rounded-2xl,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
.min-h-screen .bg-white,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80 {
  border-radius: 17px !important;
}

/* Smaller radius for some elements */
.min-h-screen .rounded-lg,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 12px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl,
  .min-h-screen .rounded-2xl,
  :deep(.program-details-modal) {
    border-radius: 14px !important;
  }
}

/* Force all text to be 16px */
.min-h-screen,
.min-h-screen *,
:deep(.program-details-modal),
:deep(.program-details-modal *) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Specific adjustments for headings */
.min-h-screen h1 {
  font-size: 28px !important;
  line-height: 1.2 !important;
}

.min-h-screen h2 {
  font-size: 24px !important;
  line-height: 1.3 !important;
}

.min-h-screen h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

/* Input and select specific text size */
.min-h-screen input,
.min-h-screen select,
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Button text size */
.min-h-screen button {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Label text size */
.min-h-screen label {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Modal specific text sizes */
:deep(.program-details-modal) h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

:deep(.program-details-modal) p {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-xs {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-sm {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-base {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Segment button text size */
:deep(.custom-segment ion-segment-button) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 24px !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
  }

  .min-h-screen input,
  .min-h-screen select,
  .min-h-screen .input-ios,
  .min-h-screen .select-ios,
  .min-h-screen button,
  .min-h-screen label {
    font-size: 16px !important;
  }
}

/* Ensure full width for all sections */
.min-h-screen > div {
  width: 100% !important;
  max-width: 100% !important;
}

/* Adjust grid layout for full width */
.lg\:grid {
  width: 100% !important;
  max-width: 100% !important;
}

/* Ensure content sections take full width */
.lg\:col-span-2,
.lg\:col-span-1 {
  width: 100% !important;
  max-width: 100% !important;
}

/* Custom card styles with !important */
.content-card {
  padding: 20px !important;
  margin: 0 8px 24px 8px !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 12px 32px 12px !important;
  }
}

.header-section {
  margin-bottom: 24px !important;
  padding: 0 16px !important;
  text-align: center !important;
}

@media (min-width: 640px) {
  .header-section {
    margin-bottom: 48px !important;
    padding: 0 24px !important;
  }
}

@media (min-width: 1024px) {
  .header-section {
    text-align: left !important;
  }
}

.exam-selection-card {
  background-color: white !important;
}

.info-panel-card,
.fun-facts-card {
  background-color: rgba(243, 244, 246, 0.8) !important;
  backdrop-filter: blur(8px) !important;
}

/* Ensure content spacing within cards */
.content-card > *:not(:last-child) {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card > *:not(:last-child) {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for nested elements */
.content-card h2,
.content-card h3 {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card h2,
  .content-card h3 {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for lists and other elements */
.content-card ul,
.content-card ol {
  margin: 12px 0 !important;
  padding-left: 24px !important;
}

.content-card li {
  margin-bottom: 8px !important;
}

/* Ensure proper spacing for buttons and interactive elements */
.content-card button {
  margin: 8px 0 !important;
}

/* Ensure proper spacing for form elements */
.content-card input,
.content-card select {
  margin-bottom: 12px !important;
}

/* Ensure proper spacing for the last element in cards */
.content-card > *:last-child {
  margin-bottom: 0 !important;
}

/* Ensure consistent grid layout */
@media (min-width: 1024px) {
  .lg\:grid {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 30px !important;
    padding: 0 15px !important;
    box-sizing: border-box !important;
  }

  .lg\:col-span-2 {
    grid-column: span 2 / span 2 !important;
  }
}

/* Adjust content card margins to align with grid */
.content-card {
  padding: 20px !important;
  margin: 0 0 24px 0 !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 0 32px 0 !important;
  }
}

/* Ensure the container takes full width */
.w-full {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* Custom border radius for all elements */
.content-card,
.header-section,
.exam-selection-card,
.info-panel-card,
.fun-facts-card,
.input-ios,
.select-ios,
button,
.rounded-lg,
.rounded-xl,
.rounded-2xl,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80,
.rounded-full {
  border-radius: 20px !important;
}

/* Specific adjustments for smaller elements */
.input-ios,
.select-ios,
button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 20px !important;
}

/* Ensure consistent border radius in modals */
:deep(.program-details-modal) {
  --border-radius: 20px 20px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 20px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .content-card,
  .header-section,
  .exam-selection-card,
  .info-panel-card,
  .fun-facts-card,
  :deep(.program-details-modal) {
    border-radius: 20px !important;
  }
}

/* Override any Tailwind border radius classes */
.rounded-lg,
.rounded-xl,
.rounded-2xl,
.rounded-full {
  border-radius: 20px !important;
}

/* Ensure buttons and inputs maintain the border radius */
button,
input,
select,
.input-ios,
.select-ios {
  border-radius: 20px !important;
}

/* Segment Container for Scrolling */
.segment-container {
  width: 100% !important;
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
  padding-bottom: 4px !important;
  position: relative !important;
}

.segment-container::-webkit-scrollbar {
  display: none !important;
}

/* Add shadow to indicate scrollable content */
.segment-container::after {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 40px !important;
  background: linear-gradient(to right, 
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 1) 100%
  ) !important;
  pointer-events: none !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  z-index: 10 !important;
}

.segment-container.has-content::after {
  opacity: 1 !important;
}

:deep(.custom-segment) {
  display: inline-flex !important;
  min-width: min-content !important;
  width: auto !important;
  overflow: visible !important;
  padding-right: 16px !important; /* Add padding to the right of the segment */
}

:deep(.custom-segment ion-segment-button) {
  white-space: nowrap !important;
}

@media (max-width: 640px) {
  .segment-container {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }

  :deep(.custom-segment) {
    padding-right: 8px !important; /* Reduce right padding on mobile */
  }
}

/* Custom Campus Segment */
.campus-segment-container {
  display: block !important;
  overflow-x: auto !important;
  white-space: nowrap !important;
  padding: 4px !important;
  background-color: rgba(235, 235, 245, 0.8) !important;
  border-radius: 12px !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important; /* Firefox */
}
.campus-segment-container::-webkit-scrollbar {
  display: none !important; /* Safari and Chrome */
}

.campus-segment-button {
  display: inline-block !important;
  padding: 8px 16px !important;
  margin: 0 2px !important;
  border: none !important;
  background-color: transparent !important;
  color: rgb(60, 60, 67, 0.6) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  border-radius: 9px !important;
  cursor: pointer !important;
  transition: all 0.2s ease-in-out !important;
}

.campus-segment-button.is-active {
  background-color: white !important;
  color: rgb(0, 122, 255) !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

/* Mobile-specific adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 28px !important;
    line-height: 1.2 !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
    line-height: 1.3 !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen p {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  .min-h-screen label {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen button {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen input,
  .min-h-screen select {
    font-size: 14px !important;
    line-height: 1.4 !important;
    padding: 8px !important;
  }

  /* Adjust card spacing and padding */
  .rounded-xl {
    border-radius: 12px !important;
  }

  .p-4 {
    padding: 16px !important;
  }

  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 16px !important;
  }

  /* Adjust grid gap */
  .gap-3 {
    gap: 12px !important;
  }

  /* Adjust icon sizes */
  .w-5 {
    width: 20px !important;
  }

  .h-5 {
    height: 20px !important;
  }

  .w-6 {
    width: 24px !important;
  }

  .h-6 {
    height: 24px !important;
  }
}

/* Adjust modal styles for mobile */
@media (max-width: 640px) {
  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }

  :deep(.program-details-modal ion-header) {
    padding: 12px;
  }

  :deep(.program-details-modal ion-content) {
    --padding-top: 16px;
    --padding-bottom: 16px;
    --padding-start: 16px;
    --padding-end: 16px;
  }

  :deep(.program-details-modal h3) {
    font-size: 19px !important;
  }

  :deep(.program-details-modal p) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-xs) {
    font-size: 13px !important;
  }

  :deep(.program-details-modal .text-sm) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-base) {
    font-size: 17px !important;
  }
}

/* Adjust segment styles for mobile */
@media (max-width: 640px) {
  :deep(.custom-segment) {
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 4px;
    --padding-end: 4px;
  }

  :deep(.custom-segment ion-segment-button) {
    font-size: 14px !important;
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 8px;
    --padding-end: 8px;
  }
}

/* Mobile-specific input styles */
@media (max-width: 640px) {
  .input-ios,
  .select-ios {
    padding: 10px 14px !important;
    min-height: 44px !important;
    font-size: 14px !important;
  }

  .input-ios::placeholder {
    font-size: 14px !important;
  }

  .select-ios {
    background-position: right 12px center !important;
    background-size: 14px !important;
    padding-right: 36px !important;
  }

  /* Adjust label spacing */
  label {
    margin-bottom: 6px !important;
  }

  /* Make buttons more touch-friendly */
  button {
    min-height: 44px !important;
    padding: 10px 16px !important;
  }

  /* Adjust spacing between exam elements */
  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 12px !important;
  }

  .gap-3 {
    gap: 12px !important;
  }
}

/* Base border radius styles */
.min-h-screen .rounded-lg {
  border-radius: 8px !important;
}

.min-h-screen .rounded-xl {
  border-radius: 12px !important;
}

.min-h-screen .rounded-2xl {
  border-radius: 16px !important;
}

/* Input and select specific styles */
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  border-radius: 8px !important;
}

/* Button specific styles */
.min-h-screen button {
  border-radius: 8px !important;
}

/* Card specific styles */
.min-h-screen .bg-white {
  border-radius: 16px !important;
}

/* Modal specific styles */
:deep(.program-details-modal) {
  --border-radius: 16px 16px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 16px !important;
}

/* Segment specific styles */
:deep(.custom-segment) {
  --border-radius: 8px !important;
}

:deep(.custom-segment ion-segment-button) {
  border-radius: 8px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl {
    border-radius: 12px !important;
  }

  .min-h-screen .rounded-2xl {
    border-radius: 14px !important;
  }

  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }
}

/* Exam entry styles */
.bg-gray-50\/50 {
  background-color: rgba(249, 250, 251, 0.5) !important;
  backdrop-filter: blur(8px) !important;
}

.border-gray-100 {
  border-color: rgba(243, 244, 246, 0.8) !important;
}

/* Add hover effect to exam entries */
.bg-gray-50\/50:hover {
  background-color: rgba(249, 250, 251, 0.7) !important;
  border-color: rgba(229, 231, 235, 0.8) !important;
  transition: all 0.2s ease-in-out;
}

/* Compact exam entry layout */
@media (min-width: 640px) {
  .exam-entry-row {
    flex-direction: row !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 0 !important;
  }
}
@media (max-width: 639px) {
  .exam-entry-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 8px !important;
  }
}

/* Special border radius class with !important */
.force-radius {
  border-radius: 17px !important;
}

.force-radius-sm {
  border-radius: 12px !important;
}

/* Apply to all elements that need border radius */
.min-h-screen .rounded-lg,
.min-h-screen .rounded-xl,
.min-h-screen .rounded-2xl,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
.min-h-screen .bg-white,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80 {
  border-radius: 17px !important;
}

/* Smaller radius for some elements */
.min-h-screen .rounded-lg,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 12px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl,
  .min-h-screen .rounded-2xl,
  :deep(.program-details-modal) {
    border-radius: 14px !important;
  }
}

/* Force all text to be 16px */
.min-h-screen,
.min-h-screen *,
:deep(.program-details-modal),
:deep(.program-details-modal *) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Specific adjustments for headings */
.min-h-screen h1 {
  font-size: 28px !important;
  line-height: 1.2 !important;
}

.min-h-screen h2 {
  font-size: 24px !important;
  line-height: 1.3 !important;
}

.min-h-screen h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

/* Input and select specific text size */
.min-h-screen input,
.min-h-screen select,
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Button text size */
.min-h-screen button {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Label text size */
.min-h-screen label {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Modal specific text sizes */
:deep(.program-details-modal) h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

:deep(.program-details-modal) p {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-xs {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-sm {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-base {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Segment button text size */
:deep(.custom-segment ion-segment-button) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 24px !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
  }

  .min-h-screen input,
  .min-h-screen select,
  .min-h-screen .input-ios,
  .min-h-screen .select-ios,
  .min-h-screen button,
  .min-h-screen label {
    font-size: 16px !important;
  }
}

/* Ensure full width for all sections */
.min-h-screen > div {
  width: 100% !important;
  max-width: 100% !important;
}

/* Adjust grid layout for full width */
.lg\:grid {
  width: 100% !important;
  max-width: 100% !important;
}

/* Ensure content sections take full width */
.lg\:col-span-2,
.lg\:col-span-1 {
  width: 100% !important;
  max-width: 100% !important;
}

/* Custom card styles with !important */
.content-card {
  padding: 20px !important;
  margin: 0 8px 24px 8px !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 12px 32px 12px !important;
  }
}

.header-section {
  margin-bottom: 24px !important;
  padding: 0 16px !important;
  text-align: center !important;
}

@media (min-width: 640px) {
  .header-section {
    margin-bottom: 48px !important;
    padding: 0 24px !important;
  }
}

@media (min-width: 1024px) {
  .header-section {
    text-align: left !important;
  }
}

.exam-selection-card {
  background-color: white !important;
}

.info-panel-card,
.fun-facts-card {
  background-color: rgba(243, 244, 246, 0.8) !important;
  backdrop-filter: blur(8px) !important;
}

/* Ensure content spacing within cards */
.content-card > *:not(:last-child) {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card > *:not(:last-child) {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for nested elements */
.content-card h2,
.content-card h3 {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card h2,
  .content-card h3 {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for lists and other elements */
.content-card ul,
.content-card ol {
  margin: 12px 0 !important;
  padding-left: 24px !important;
}

.content-card li {
  margin-bottom: 8px !important;
}

/* Ensure proper spacing for buttons and interactive elements */
.content-card button {
  margin: 8px 0 !important;
}

/* Ensure proper spacing for form elements */
.content-card input,
.content-card select {
  margin-bottom: 12px !important;
}

/* Ensure proper spacing for the last element in cards */
.content-card > *:last-child {
  margin-bottom: 0 !important;
}

/* Ensure consistent grid layout */
@media (min-width: 1024px) {
  .lg\:grid {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 30px !important;
    padding: 0 15px !important;
    box-sizing: border-box !important;
  }

  .lg\:col-span-2 {
    grid-column: span 2 / span 2 !important;
  }
}

/* Adjust content card margins to align with grid */
.content-card {
  padding: 20px !important;
  margin: 0 0 24px 0 !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 0 32px 0 !important;
  }
}

/* Ensure the container takes full width */
.w-full {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* Custom border radius for all elements */
.content-card,
.header-section,
.exam-selection-card,
.info-panel-card,
.fun-facts-card,
.input-ios,
.select-ios,
button,
.rounded-lg,
.rounded-xl,
.rounded-2xl,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80,
.rounded-full {
  border-radius: 20px !important;
}

/* Specific adjustments for smaller elements */
.input-ios,
.select-ios,
button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 20px !important;
}

/* Ensure consistent border radius in modals */
:deep(.program-details-modal) {
  --border-radius: 20px 20px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 20px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .content-card,
  .header-section,
  .exam-selection-card,
  .info-panel-card,
  .fun-facts-card,
  :deep(.program-details-modal) {
    border-radius: 20px !important;
  }
}

/* Override any Tailwind border radius classes */
.rounded-lg,
.rounded-xl,
.rounded-2xl,
.rounded-full {
  border-radius: 20px !important;
}

/* Ensure buttons and inputs maintain the border radius */
button,
input,
select,
.input-ios,
.select-ios {
  border-radius: 20px !important;
}

/* Segment Container for Scrolling */
.segment-container {
  width: 100% !important;
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
  padding-bottom: 4px !important;
  position: relative !important;
}

.segment-container::-webkit-scrollbar {
  display: none !important;
}

/* Add shadow to indicate scrollable content */
.segment-container::after {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 40px !important;
  background: linear-gradient(to right, 
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 1) 100%
  ) !important;
  pointer-events: none !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  z-index: 10 !important;
}

.segment-container.has-content::after {
  opacity: 1 !important;
}

:deep(.custom-segment) {
  display: inline-flex !important;
  min-width: min-content !important;
  width: auto !important;
  overflow: visible !important;
  padding-right: 16px !important; /* Add padding to the right of the segment */
}

:deep(.custom-segment ion-segment-button) {
  white-space: nowrap !important;
}

@media (max-width: 640px) {
  .segment-container {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }

  :deep(.custom-segment) {
    padding-right: 8px !important; /* Reduce right padding on mobile */
  }
}

/* Custom Campus Segment */
.campus-segment-container {
  display: block !important;
  overflow-x: auto !important;
  white-space: nowrap !important;
  padding: 4px !important;
  background-color: rgba(235, 235, 245, 0.8) !important;
  border-radius: 12px !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important; /* Firefox */
}
.campus-segment-container::-webkit-scrollbar {
  display: none !important; /* Safari and Chrome */
}

.campus-segment-button {
  display: inline-block !important;
  padding: 8px 16px !important;
  margin: 0 2px !important;
  border: none !important;
  background-color: transparent !important;
  color: rgb(60, 60, 67, 0.6) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  border-radius: 9px !important;
  cursor: pointer !important;
  transition: all 0.2s ease-in-out !important;
}

.campus-segment-button.is-active {
  background-color: white !important;
  color: rgb(0, 122, 255) !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

/* Mobile-specific adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 28px !important;
    line-height: 1.2 !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
    line-height: 1.3 !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen p {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  .min-h-screen label {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen button {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen input,
  .min-h-screen select {
    font-size: 14px !important;
    line-height: 1.4 !important;
    padding: 8px !important;
  }

  /* Adjust card spacing and padding */
  .rounded-xl {
    border-radius: 12px !important;
  }

  .p-4 {
    padding: 16px !important;
  }

  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 16px !important;
  }

  /* Adjust grid gap */
  .gap-3 {
    gap: 12px !important;
  }

  /* Adjust icon sizes */
  .w-5 {
    width: 20px !important;
  }

  .h-5 {
    height: 20px !important;
  }

  .w-6 {
    width: 24px !important;
  }

  .h-6 {
    height: 24px !important;
  }
}

/* Adjust modal styles for mobile */
@media (max-width: 640px) {
  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }

  :deep(.program-details-modal ion-header) {
    padding: 12px;
  }

  :deep(.program-details-modal ion-content) {
    --padding-top: 16px;
    --padding-bottom: 16px;
    --padding-start: 16px;
    --padding-end: 16px;
  }

  :deep(.program-details-modal h3) {
    font-size: 19px !important;
  }

  :deep(.program-details-modal p) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-xs) {
    font-size: 13px !important;
  }

  :deep(.program-details-modal .text-sm) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-base) {
    font-size: 17px !important;
  }
}

/* Adjust segment styles for mobile */
@media (max-width: 640px) {
  :deep(.custom-segment) {
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 4px;
    --padding-end: 4px;
  }

  :deep(.custom-segment ion-segment-button) {
    font-size: 14px !important;
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 8px;
    --padding-end: 8px;
  }
}

/* Mobile-specific input styles */
@media (max-width: 640px) {
  .input-ios,
  .select-ios {
    padding: 10px 14px !important;
    min-height: 44px !important;
    font-size: 14px !important;
  }

  .input-ios::placeholder {
    font-size: 14px !important;
  }

  .select-ios {
    background-position: right 12px center !important;
    background-size: 14px !important;
    padding-right: 36px !important;
  }

  /* Adjust label spacing */
  label {
    margin-bottom: 6px !important;
  }

  /* Make buttons more touch-friendly */
  button {
    min-height: 44px !important;
    padding: 10px 16px !important;
  }

  /* Adjust spacing between exam elements */
  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 12px !important;
  }

  .gap-3 {
    gap: 12px !important;
  }
}

/* Base border radius styles */
.min-h-screen .rounded-lg {
  border-radius: 8px !important;
}

.min-h-screen .rounded-xl {
  border-radius: 12px !important;
}

.min-h-screen .rounded-2xl {
  border-radius: 16px !important;
}

/* Input and select specific styles */
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  border-radius: 8px !important;
}

/* Button specific styles */
.min-h-screen button {
  border-radius: 8px !important;
}

/* Card specific styles */
.min-h-screen .bg-white {
  border-radius: 16px !important;
}

/* Modal specific styles */
:deep(.program-details-modal) {
  --border-radius: 16px 16px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 16px !important;
}

/* Segment specific styles */
:deep(.custom-segment) {
  --border-radius: 8px !important;
}

:deep(.custom-segment ion-segment-button) {
  border-radius: 8px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl {
    border-radius: 12px !important;
  }

  .min-h-screen .rounded-2xl {
    border-radius: 14px !important;
  }

  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }
}

/* Exam entry styles */
.bg-gray-50\/50 {
  background-color: rgba(249, 250, 251, 0.5) !important;
  backdrop-filter: blur(8px) !important;
}

.border-gray-100 {
  border-color: rgba(243, 244, 246, 0.8) !important;
}

/* Add hover effect to exam entries */
.bg-gray-50\/50:hover {
  background-color: rgba(249, 250, 251, 0.7) !important;
  border-color: rgba(229, 231, 235, 0.8) !important;
  transition: all 0.2s ease-in-out;
}

/* Compact exam entry layout */
@media (min-width: 640px) {
  .exam-entry-row {
    flex-direction: row !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 0 !important;
  }
}
@media (max-width: 639px) {
  .exam-entry-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 8px !important;
  }
}

/* Special border radius class with !important */
.force-radius {
  border-radius: 17px !important;
}

.force-radius-sm {
  border-radius: 12px !important;
}

/* Apply to all elements that need border radius */
.min-h-screen .rounded-lg,
.min-h-screen .rounded-xl,
.min-h-screen .rounded-2xl,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
.min-h-screen .bg-white,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80 {
  border-radius: 17px !important;
}

/* Smaller radius for some elements */
.min-h-screen .rounded-lg,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 12px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl,
  .min-h-screen .rounded-2xl,
  :deep(.program-details-modal) {
    border-radius: 14px !important;
  }
}

/* Force all text to be 16px */
.min-h-screen,
.min-h-screen *,
:deep(.program-details-modal),
:deep(.program-details-modal *) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Specific adjustments for headings */
.min-h-screen h1 {
  font-size: 28px !important;
  line-height: 1.2 !important;
}

.min-h-screen h2 {
  font-size: 24px !important;
  line-height: 1.3 !important;
}

.min-h-screen h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

/* Input and select specific text size */
.min-h-screen input,
.min-h-screen select,
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Button text size */
.min-h-screen button {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Label text size */
.min-h-screen label {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Modal specific text sizes */
:deep(.program-details-modal) h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

:deep(.program-details-modal) p {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-xs {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-sm {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-base {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Segment button text size */
:deep(.custom-segment ion-segment-button) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 24px !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
  }

  .min-h-screen input,
  .min-h-screen select,
  .min-h-screen .input-ios,
  .min-h-screen .select-ios,
  .min-h-screen button,
  .min-h-screen label {
    font-size: 16px !important;
  }
}

/* Ensure full width for all sections */
.min-h-screen > div {
  width: 100% !important;
  max-width: 100% !important;
}

/* Adjust grid layout for full width */
.lg\:grid {
  width: 100% !important;
  max-width: 100% !important;
}

/* Ensure content sections take full width */
.lg\:col-span-2,
.lg\:col-span-1 {
  width: 100% !important;
  max-width: 100% !important;
}

/* Custom card styles with !important */
.content-card {
  padding: 20px !important;
  margin: 0 8px 24px 8px !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 12px 32px 12px !important;
  }
}

.header-section {
  margin-bottom: 24px !important;
  padding: 0 16px !important;
  text-align: center !important;
}

@media (min-width: 640px) {
  .header-section {
    margin-bottom: 48px !important;
    padding: 0 24px !important;
  }
}

@media (min-width: 1024px) {
  .header-section {
    text-align: left !important;
  }
}

.exam-selection-card {
  background-color: white !important;
}

.info-panel-card,
.fun-facts-card {
  background-color: rgba(243, 244, 246, 0.8) !important;
  backdrop-filter: blur(8px) !important;
}

/* Ensure content spacing within cards */
.content-card > *:not(:last-child) {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card > *:not(:last-child) {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for nested elements */
.content-card h2,
.content-card h3 {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card h2,
  .content-card h3 {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for lists and other elements */
.content-card ul,
.content-card ol {
  margin: 12px 0 !important;
  padding-left: 24px !important;
}

.content-card li {
  margin-bottom: 8px !important;
}

/* Ensure proper spacing for buttons and interactive elements */
.content-card button {
  margin: 8px 0 !important;
}

/* Ensure proper spacing for form elements */
.content-card input,
.content-card select {
  margin-bottom: 12px !important;
}

/* Ensure proper spacing for the last element in cards */
.content-card > *:last-child {
  margin-bottom: 0 !important;
}

/* Ensure consistent grid layout */
@media (min-width: 1024px) {
  .lg\:grid {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 30px !important;
    padding: 0 15px !important;
    box-sizing: border-box !important;
  }

  .lg\:col-span-2 {
    grid-column: span 2 / span 2 !important;
  }
}

/* Adjust content card margins to align with grid */
.content-card {
  padding: 20px !important;
  margin: 0 0 24px 0 !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 0 32px 0 !important;
  }
}

/* Ensure the container takes full width */
.w-full {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* Custom border radius for all elements */
.content-card,
.header-section,
.exam-selection-card,
.info-panel-card,
.fun-facts-card,
.input-ios,
.select-ios,
button,
.rounded-lg,
.rounded-xl,
.rounded-2xl,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80,
.rounded-full {
  border-radius: 20px !important;
}

/* Specific adjustments for smaller elements */
.input-ios,
.select-ios,
button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 20px !important;
}

/* Ensure consistent border radius in modals */
:deep(.program-details-modal) {
  --border-radius: 20px 20px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 20px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .content-card,
  .header-section,
  .exam-selection-card,
  .info-panel-card,
  .fun-facts-card,
  :deep(.program-details-modal) {
    border-radius: 20px !important;
  }
}

/* Override any Tailwind border radius classes */
.rounded-lg,
.rounded-xl,
.rounded-2xl,
.rounded-full {
  border-radius: 20px !important;
}

/* Ensure buttons and inputs maintain the border radius */
button,
input,
select,
.input-ios,
.select-ios {
  border-radius: 20px !important;
}

/* Segment Container for Scrolling */
.segment-container {
  width: 100% !important;
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
  padding-bottom: 4px !important;
  position: relative !important;
}

.segment-container::-webkit-scrollbar {
  display: none !important;
}

/* Add shadow to indicate scrollable content */
.segment-container::after {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 40px !important;
  background: linear-gradient(to right, 
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 1) 100%
  ) !important;
  pointer-events: none !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  z-index: 10 !important;
}

.segment-container.has-content::after {
  opacity: 1 !important;
}

:deep(.custom-segment) {
  display: inline-flex !important;
  min-width: min-content !important;
  width: auto !important;
  overflow: visible !important;
  padding-right: 16px !important; /* Add padding to the right of the segment */
}

:deep(.custom-segment ion-segment-button) {
  white-space: nowrap !important;
}

@media (max-width: 640px) {
  .segment-container {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }

  :deep(.custom-segment) {
    padding-right: 8px !important; /* Reduce right padding on mobile */
  }
}

/* Custom Campus Segment */
.campus-segment-container {
  display: block !important;
  overflow-x: auto !important;
  white-space: nowrap !important;
  padding: 4px !important;
  background-color: rgba(235, 235, 245, 0.8) !important;
  border-radius: 12px !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important; /* Firefox */
}
.campus-segment-container::-webkit-scrollbar {
  display: none !important; /* Safari and Chrome */
}

.campus-segment-button {
  display: inline-block !important;
  padding: 8px 16px !important;
  margin: 0 2px !important;
  border: none !important;
  background-color: transparent !important;
  color: rgb(60, 60, 67, 0.6) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  border-radius: 9px !important;
  cursor: pointer !important;
  transition: all 0.2s ease-in-out !important;
}

.campus-segment-button.is-active {
  background-color: white !important;
  color: rgb(0, 122, 255) !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

/* Mobile-specific adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 28px !important;
    line-height: 1.2 !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
    line-height: 1.3 !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen p {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  .min-h-screen label {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen button {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen input,
  .min-h-screen select {
    font-size: 14px !important;
    line-height: 1.4 !important;
    padding: 8px !important;
  }

  /* Adjust card spacing and padding */
  .rounded-xl {
    border-radius: 12px !important;
  }

  .p-4 {
    padding: 16px !important;
  }

  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 16px !important;
  }

  /* Adjust grid gap */
  .gap-3 {
    gap: 12px !important;
  }

  /* Adjust icon sizes */
  .w-5 {
    width: 20px !important;
  }

  .h-5 {
    height: 20px !important;
  }

  .w-6 {
    width: 24px !important;
  }

  .h-6 {
    height: 24px !important;
  }
}

/* Adjust modal styles for mobile */
@media (max-width: 640px) {
  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }

  :deep(.program-details-modal ion-header) {
    padding: 12px;
  }

  :deep(.program-details-modal ion-content) {
    --padding-top: 16px;
    --padding-bottom: 16px;
    --padding-start: 16px;
    --padding-end: 16px;
  }

  :deep(.program-details-modal h3) {
    font-size: 19px !important;
  }

  :deep(.program-details-modal p) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-xs) {
    font-size: 13px !important;
  }

  :deep(.program-details-modal .text-sm) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-base) {
    font-size: 17px !important;
  }
}

/* Adjust segment styles for mobile */
@media (max-width: 640px) {
  :deep(.custom-segment) {
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 4px;
    --padding-end: 4px;
  }

  :deep(.custom-segment ion-segment-button) {
    font-size: 14px !important;
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 8px;
    --padding-end: 8px;
  }
}

/* Mobile-specific input styles */
@media (max-width: 640px) {
  .input-ios,
  .select-ios {
    padding: 10px 14px !important;
    min-height: 44px !important;
    font-size: 14px !important;
  }

  .input-ios::placeholder {
    font-size: 14px !important;
  }

  .select-ios {
    background-position: right 12px center !important;
    background-size: 14px !important;
    padding-right: 36px !important;
  }

  /* Adjust label spacing */
  label {
    margin-bottom: 6px !important;
  }

  /* Make buttons more touch-friendly */
  button {
    min-height: 44px !important;
    padding: 10px 16px !important;
  }

  /* Adjust spacing between exam elements */
  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 12px !important;
  }

  .gap-3 {
    gap: 12px !important;
  }
}

/* Base border radius styles */
.min-h-screen .rounded-lg {
  border-radius: 8px !important;
}

.min-h-screen .rounded-xl {
  border-radius: 12px !important;
}

.min-h-screen .rounded-2xl {
  border-radius: 16px !important;
}

/* Input and select specific styles */
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  border-radius: 8px !important;
}

/* Button specific styles */
.min-h-screen button {
  border-radius: 8px !important;
}

/* Card specific styles */
.min-h-screen .bg-white {
  border-radius: 16px !important;
}

/* Modal specific styles */
:deep(.program-details-modal) {
  --border-radius: 16px 16px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 16px !important;
}

/* Segment specific styles */
:deep(.custom-segment) {
  --border-radius: 8px !important;
}

:deep(.custom-segment ion-segment-button) {
  border-radius: 8px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl {
    border-radius: 12px !important;
  }

  .min-h-screen .rounded-2xl {
    border-radius: 14px !important;
  }

  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }
}

/* Exam entry styles */
.bg-gray-50\/50 {
  background-color: rgba(249, 250, 251, 0.5) !important;
  backdrop-filter: blur(8px) !important;
}

.border-gray-100 {
  border-color: rgba(243, 244, 246, 0.8) !important;
}

/* Add hover effect to exam entries */
.bg-gray-50\/50:hover {
  background-color: rgba(249, 250, 251, 0.7) !important;
  border-color: rgba(229, 231, 235, 0.8) !important;
  transition: all 0.2s ease-in-out;
}

/* Compact exam entry layout */
@media (min-width: 640px) {
  .exam-entry-row {
    flex-direction: row !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 0 !important;
  }
}
@media (max-width: 639px) {
  .exam-entry-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 8px !important;
  }
}

/* Special border radius class with !important */
.force-radius {
  border-radius: 17px !important;
}

.force-radius-sm {
  border-radius: 12px !important;
}

/* Apply to all elements that need border radius */
.min-h-screen .rounded-lg,
.min-h-screen .rounded-xl,
.min-h-screen .rounded-2xl,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
.min-h-screen .bg-white,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80 {
  border-radius: 17px !important;
}

/* Smaller radius for some elements */
.min-h-screen .rounded-lg,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 12px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl,
  .min-h-screen .rounded-2xl,
  :deep(.program-details-modal) {
    border-radius: 14px !important;
  }
}

/* Force all text to be 16px */
.min-h-screen,
.min-h-screen *,
:deep(.program-details-modal),
:deep(.program-details-modal *) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Specific adjustments for headings */
.min-h-screen h1 {
  font-size: 28px !important;
  line-height: 1.2 !important;
}

.min-h-screen h2 {
  font-size: 24px !important;
  line-height: 1.3 !important;
}

.min-h-screen h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

/* Input and select specific text size */
.min-h-screen input,
.min-h-screen select,
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Button text size */
.min-h-screen button {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Label text size */
.min-h-screen label {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Modal specific text sizes */
:deep(.program-details-modal) h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

:deep(.program-details-modal) p {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-xs {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-sm {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-base {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Segment button text size */
:deep(.custom-segment ion-segment-button) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 24px !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
  }

  .min-h-screen input,
  .min-h-screen select,
  .min-h-screen .input-ios,
  .min-h-screen .select-ios,
  .min-h-screen button,
  .min-h-screen label {
    font-size: 16px !important;
  }
}

/* Ensure full width for all sections */
.min-h-screen > div {
  width: 100% !important;
  max-width: 100% !important;
}

/* Adjust grid layout for full width */
.lg\:grid {
  width: 100% !important;
  max-width: 100% !important;
}

/* Ensure content sections take full width */
.lg\:col-span-2,
.lg\:col-span-1 {
  width: 100% !important;
  max-width: 100% !important;
}

/* Custom card styles with !important */
.content-card {
  padding: 20px !important;
  margin: 0 8px 24px 8px !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 12px 32px 12px !important;
  }
}

.header-section {
  margin-bottom: 24px !important;
  padding: 0 16px !important;
  text-align: center !important;
}

@media (min-width: 640px) {
  .header-section {
    margin-bottom: 48px !important;
    padding: 0 24px !important;
  }
}

@media (min-width: 1024px) {
  .header-section {
    text-align: left !important;
  }
}

.exam-selection-card {
  background-color: white !important;
}

.info-panel-card,
.fun-facts-card {
  background-color: rgba(243, 244, 246, 0.8) !important;
  backdrop-filter: blur(8px) !important;
}

/* Ensure content spacing within cards */
.content-card > *:not(:last-child) {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card > *:not(:last-child) {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for nested elements */
.content-card h2,
.content-card h3 {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card h2,
  .content-card h3 {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for lists and other elements */
.content-card ul,
.content-card ol {
  margin: 12px 0 !important;
  padding-left: 24px !important;
}

.content-card li {
  margin-bottom: 8px !important;
}

/* Ensure proper spacing for buttons and interactive elements */
.content-card button {
  margin: 8px 0 !important;
}

/* Ensure proper spacing for form elements */
.content-card input,
.content-card select {
  margin-bottom: 12px !important;
}

/* Ensure proper spacing for the last element in cards */
.content-card > *:last-child {
  margin-bottom: 0 !important;
}

/* Ensure consistent grid layout */
@media (min-width: 1024px) {
  .lg\:grid {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 30px !important;
    padding: 0 15px !important;
    box-sizing: border-box !important;
  }

  .lg\:col-span-2 {
    grid-column: span 2 / span 2 !important;
  }
}

/* Adjust content card margins to align with grid */
.content-card {
  padding: 20px !important;
  margin: 0 0 24px 0 !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 0 32px 0 !important;
  }
}

/* Ensure the container takes full width */
.w-full {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* Custom border radius for all elements */
.content-card,
.header-section,
.exam-selection-card,
.info-panel-card,
.fun-facts-card,
.input-ios,
.select-ios,
button,
.rounded-lg,
.rounded-xl,
.rounded-2xl,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80,
.rounded-full {
  border-radius: 20px !important;
}

/* Specific adjustments for smaller elements */
.input-ios,
.select-ios,
button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 20px !important;
}

/* Ensure consistent border radius in modals */
:deep(.program-details-modal) {
  --border-radius: 20px 20px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 20px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .content-card,
  .header-section,
  .exam-selection-card,
  .info-panel-card,
  .fun-facts-card,
  :deep(.program-details-modal) {
    border-radius: 20px !important;
  }
}

/* Override any Tailwind border radius classes */
.rounded-lg,
.rounded-xl,
.rounded-2xl,
.rounded-full {
  border-radius: 20px !important;
}

/* Ensure buttons and inputs maintain the border radius */
button,
input,
select,
.input-ios,
.select-ios {
  border-radius: 20px !important;
}

/* Segment Container for Scrolling */
.segment-container {
  width: 100% !important;
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
  padding-bottom: 4px !important;
  position: relative !important;
}

.segment-container::-webkit-scrollbar {
  display: none !important;
}

/* Add shadow to indicate scrollable content */
.segment-container::after {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 40px !important;
  background: linear-gradient(to right, 
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 1) 100%
  ) !important;
  pointer-events: none !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  z-index: 10 !important;
}

.segment-container.has-content::after {
  opacity: 1 !important;
}

:deep(.custom-segment) {
  display: inline-flex !important;
  min-width: min-content !important;
  width: auto !important;
  overflow: visible !important;
  padding-right: 16px !important; /* Add padding to the right of the segment */
}

:deep(.custom-segment ion-segment-button) {
  white-space: nowrap !important;
}

@media (max-width: 640px) {
  .segment-container {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }

  :deep(.custom-segment) {
    padding-right: 8px !important; /* Reduce right padding on mobile */
  }
}

/* Custom Campus Segment */
.campus-segment-container {
  display: block !important;
  overflow-x: auto !important;
  white-space: nowrap !important;
  padding: 4px !important;
  background-color: rgba(235, 235, 245, 0.8) !important;
  border-radius: 12px !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important; /* Firefox */
}
.campus-segment-container::-webkit-scrollbar {
  display: none !important; /* Safari and Chrome */
}

.campus-segment-button {
  display: inline-block !important;
  padding: 8px 16px !important;
  margin: 0 2px !important;
  border: none !important;
  background-color: transparent !important;
  color: rgb(60, 60, 67, 0.6) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  border-radius: 9px !important;
  cursor: pointer !important;
  transition: all 0.2s ease-in-out !important;
}

.campus-segment-button.is-active {
  background-color: white !important;
  color: rgb(0, 122, 255) !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

/* Mobile-specific adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 28px !important;
    line-height: 1.2 !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
    line-height: 1.3 !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen p {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  .min-h-screen label {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen button {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen input,
  .min-h-screen select {
    font-size: 14px !important;
    line-height: 1.4 !important;
    padding: 8px !important;
  }

  /* Adjust card spacing and padding */
  .rounded-xl {
    border-radius: 12px !important;
  }

  .p-4 {
    padding: 16px !important;
  }

  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 16px !important;
  }

  /* Adjust grid gap */
  .gap-3 {
    gap: 12px !important;
  }

  /* Adjust icon sizes */
  .w-5 {
    width: 20px !important;
  }

  .h-5 {
    height: 20px !important;
  }

  .w-6 {
    width: 24px !important;
  }

  .h-6 {
    height: 24px !important;
  }
}

/* Adjust modal styles for mobile */
@media (max-width: 640px) {
  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }

  :deep(.program-details-modal ion-header) {
    padding: 12px;
  }

  :deep(.program-details-modal ion-content) {
    --padding-top: 16px;
    --padding-bottom: 16px;
    --padding-start: 16px;
    --padding-end: 16px;
  }

  :deep(.program-details-modal h3) {
    font-size: 19px !important;
  }

  :deep(.program-details-modal p) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-xs) {
    font-size: 13px !important;
  }

  :deep(.program-details-modal .text-sm) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-base) {
    font-size: 17px !important;
  }
}

/* Adjust segment styles for mobile */
@media (max-width: 640px) {
  :deep(.custom-segment) {
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 4px;
    --padding-end: 4px;
  }

  :deep(.custom-segment ion-segment-button) {
    font-size: 14px !important;
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 8px;
    --padding-end: 8px;
  }
}

/* Mobile-specific input styles */
@media (max-width: 640px) {
  .input-ios,
  .select-ios {
    padding: 10px 14px !important;
    min-height: 44px !important;
    font-size: 14px !important;
  }

  .input-ios::placeholder {
    font-size: 14px !important;
  }

  .select-ios {
    background-position: right 12px center !important;
    background-size: 14px !important;
    padding-right: 36px !important;
  }

  /* Adjust label spacing */
  label {
    margin-bottom: 6px !important;
  }

  /* Make buttons more touch-friendly */
  button {
    min-height: 44px !important;
    padding: 10px 16px !important;
  }

  /* Adjust spacing between exam elements */
  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 12px !important;
  }

  .gap-3 {
    gap: 12px !important;
  }
}

/* Base border radius styles */
.min-h-screen .rounded-lg {
  border-radius: 8px !important;
}

.min-h-screen .rounded-xl {
  border-radius: 12px !important;
}

.min-h-screen .rounded-2xl {
  border-radius: 16px !important;
}

/* Input and select specific styles */
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  border-radius: 8px !important;
}

/* Button specific styles */
.min-h-screen button {
  border-radius: 8px !important;
}

/* Card specific styles */
.min-h-screen .bg-white {
  border-radius: 16px !important;
}

/* Modal specific styles */
:deep(.program-details-modal) {
  --border-radius: 16px 16px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 16px !important;
}

/* Segment specific styles */
:deep(.custom-segment) {
  --border-radius: 8px !important;
}

:deep(.custom-segment ion-segment-button) {
  border-radius: 8px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl {
    border-radius: 12px !important;
  }

  .min-h-screen .rounded-2xl {
    border-radius: 14px !important;
  }

  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }
}

/* Exam entry styles */
.bg-gray-50\/50 {
  background-color: rgba(249, 250, 251, 0.5) !important;
  backdrop-filter: blur(8px) !important;
}

.border-gray-100 {
  border-color: rgba(243, 244, 246, 0.8) !important;
}

/* Add hover effect to exam entries */
.bg-gray-50\/50:hover {
  background-color: rgba(249, 250, 251, 0.7) !important;
  border-color: rgba(229, 231, 235, 0.8) !important;
  transition: all 0.2s ease-in-out;
}

/* Compact exam entry layout */
@media (min-width: 640px) {
  .exam-entry-row {
    flex-direction: row !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 0 !important;
  }
}
@media (max-width: 639px) {
  .exam-entry-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 8px !important;
  }
}

/* Special border radius class with !important */
.force-radius {
  border-radius: 17px !important;
}

.force-radius-sm {
  border-radius: 12px !important;
}

/* Apply to all elements that need border radius */
.min-h-screen .rounded-lg,
.min-h-screen .rounded-xl,
.min-h-screen .rounded-2xl,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
.min-h-screen .bg-white,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80 {
  border-radius: 17px !important;
}

/* Smaller radius for some elements */
.min-h-screen .rounded-lg,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 12px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl,
  .min-h-screen .rounded-2xl,
  :deep(.program-details-modal) {
    border-radius: 14px !important;
  }
}

/* Force all text to be 16px */
.min-h-screen,
.min-h-screen *,
:deep(.program-details-modal),
:deep(.program-details-modal *) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Specific adjustments for headings */
.min-h-screen h1 {
  font-size: 28px !important;
  line-height: 1.2 !important;
}

.min-h-screen h2 {
  font-size: 24px !important;
  line-height: 1.3 !important;
}

.min-h-screen h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

/* Input and select specific text size */
.min-h-screen input,
.min-h-screen select,
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Button text size */
.min-h-screen button {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Label text size */
.min-h-screen label {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Modal specific text sizes */
:deep(.program-details-modal) h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

:deep(.program-details-modal) p {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-xs {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-sm {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-base {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Segment button text size */
:deep(.custom-segment ion-segment-button) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 24px !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
  }

  .min-h-screen input,
  .min-h-screen select,
  .min-h-screen .input-ios,
  .min-h-screen .select-ios,
  .min-h-screen button,
  .min-h-screen label {
    font-size: 16px !important;
  }
}

/* Ensure full width for all sections */
.min-h-screen > div {
  width: 100% !important;
  max-width: 100% !important;
}

/* Adjust grid layout for full width */
.lg\:grid {
  width: 100% !important;
  max-width: 100% !important;
}

/* Ensure content sections take full width */
.lg\:col-span-2,
.lg\:col-span-1 {
  width: 100% !important;
  max-width: 100% !important;
}

/* Custom card styles with !important */
.content-card {
  padding: 20px !important;
  margin: 0 8px 24px 8px !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 12px 32px 12px !important;
  }
}

.header-section {
  margin-bottom: 24px !important;
  padding: 0 16px !important;
  text-align: center !important;
}

@media (min-width: 640px) {
  .header-section {
    margin-bottom: 48px !important;
    padding: 0 24px !important;
  }
}

@media (min-width: 1024px) {
  .header-section {
    text-align: left !important;
  }
}

.exam-selection-card {
  background-color: white !important;
}

.info-panel-card,
.fun-facts-card {
  background-color: rgba(243, 244, 246, 0.8) !important;
  backdrop-filter: blur(8px) !important;
}

/* Ensure content spacing within cards */
.content-card > *:not(:last-child) {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card > *:not(:last-child) {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for nested elements */
.content-card h2,
.content-card h3 {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card h2,
  .content-card h3 {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for lists and other elements */
.content-card ul,
.content-card ol {
  margin: 12px 0 !important;
  padding-left: 24px !important;
}

.content-card li {
  margin-bottom: 8px !important;
}

/* Ensure proper spacing for buttons and interactive elements */
.content-card button {
  margin: 8px 0 !important;
}

/* Ensure proper spacing for form elements */
.content-card input,
.content-card select {
  margin-bottom: 12px !important;
}

/* Ensure proper spacing for the last element in cards */
.content-card > *:last-child {
  margin-bottom: 0 !important;
}

/* Ensure consistent grid layout */
@media (min-width: 1024px) {
  .lg\:grid {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 30px !important;
    padding: 0 15px !important;
    box-sizing: border-box !important;
  }

  .lg\:col-span-2 {
    grid-column: span 2 / span 2 !important;
  }
}

/* Adjust content card margins to align with grid */
.content-card {
  padding: 20px !important;
  margin: 0 0 24px 0 !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 0 32px 0 !important;
  }
}

/* Ensure the container takes full width */
.w-full {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* Custom border radius for all elements */
.content-card,
.header-section,
.exam-selection-card,
.info-panel-card,
.fun-facts-card,
.input-ios,
.select-ios,
button,
.rounded-lg,
.rounded-xl,
.rounded-2xl,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80,
.rounded-full {
  border-radius: 20px !important;
}

/* Specific adjustments for smaller elements */
.input-ios,
.select-ios,
button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 20px !important;
}

/* Ensure consistent border radius in modals */
:deep(.program-details-modal) {
  --border-radius: 20px 20px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 20px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .content-card,
  .header-section,
  .exam-selection-card,
  .info-panel-card,
  .fun-facts-card,
  :deep(.program-details-modal) {
    border-radius: 20px !important;
  }
}

/* Override any Tailwind border radius classes */
.rounded-lg,
.rounded-xl,
.rounded-2xl,
.rounded-full {
  border-radius: 20px !important;
}

/* Ensure buttons and inputs maintain the border radius */
button,
input,
select,
.input-ios,
.select-ios {
  border-radius: 20px !important;
}

/* Segment Container for Scrolling */
.segment-container {
  width: 100% !important;
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
  padding-bottom: 4px !important;
  position: relative !important;
}

.segment-container::-webkit-scrollbar {
  display: none !important;
}

/* Add shadow to indicate scrollable content */
.segment-container::after {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 40px !important;
  background: linear-gradient(to right, 
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 1) 100%
  ) !important;
  pointer-events: none !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  z-index: 10 !important;
}

.segment-container.has-content::after {
  opacity: 1 !important;
}

:deep(.custom-segment) {
  display: inline-flex !important;
  min-width: min-content !important;
  width: auto !important;
  overflow: visible !important;
  padding-right: 16px !important; /* Add padding to the right of the segment */
}

:deep(.custom-segment ion-segment-button) {
  white-space: nowrap !important;
}

@media (max-width: 640px) {
  .segment-container {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }

  :deep(.custom-segment) {
    padding-right: 8px !important; /* Reduce right padding on mobile */
  }
}

/* Custom Campus Segment */
.campus-segment-container {
  display: block !important;
  overflow-x: auto !important;
  white-space: nowrap !important;
  padding: 4px !important;
  background-color: rgba(235, 235, 245, 0.8) !important;
  border-radius: 12px !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important; /* Firefox */
}
.campus-segment-container::-webkit-scrollbar {
  display: none !important; /* Safari and Chrome */
}

.campus-segment-button {
  display: inline-block !important;
  padding: 8px 16px !important;
  margin: 0 2px !important;
  border: none !important;
  background-color: transparent !important;
  color: rgb(60, 60, 67, 0.6) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  border-radius: 9px !important;
  cursor: pointer !important;
  transition: all 0.2s ease-in-out !important;
}

.campus-segment-button.is-active {
  background-color: white !important;
  color: rgb(0, 122, 255) !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

/* Mobile-specific adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 28px !important;
    line-height: 1.2 !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
    line-height: 1.3 !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen p {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  .min-h-screen label {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen button {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen input,
  .min-h-screen select {
    font-size: 14px !important;
    line-height: 1.4 !important;
    padding: 8px !important;
  }

  /* Adjust card spacing and padding */
  .rounded-xl {
    border-radius: 12px !important;
  }

  .p-4 {
    padding: 16px !important;
  }

  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 16px !important;
  }

  /* Adjust grid gap */
  .gap-3 {
    gap: 12px !important;
  }

  /* Adjust icon sizes */
  .w-5 {
    width: 20px !important;
  }

  .h-5 {
    height: 20px !important;
  }

  .w-6 {
    width: 24px !important;
  }

  .h-6 {
    height: 24px !important;
  }
}

/* Adjust modal styles for mobile */
@media (max-width: 640px) {
  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }

  :deep(.program-details-modal ion-header) {
    padding: 12px;
  }

  :deep(.program-details-modal ion-content) {
    --padding-top: 16px;
    --padding-bottom: 16px;
    --padding-start: 16px;
    --padding-end: 16px;
  }

  :deep(.program-details-modal h3) {
    font-size: 19px !important;
  }

  :deep(.program-details-modal p) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-xs) {
    font-size: 13px !important;
  }

  :deep(.program-details-modal .text-sm) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-base) {
    font-size: 17px !important;
  }
}

/* Adjust segment styles for mobile */
@media (max-width: 640px) {
  :deep(.custom-segment) {
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 4px;
    --padding-end: 4px;
  }

  :deep(.custom-segment ion-segment-button) {
    font-size: 14px !important;
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 8px;
    --padding-end: 8px;
  }
}

/* Mobile-specific input styles */
@media (max-width: 640px) {
  .input-ios,
  .select-ios {
    padding: 10px 14px !important;
    min-height: 44px !important;
    font-size: 14px !important;
  }

  .input-ios::placeholder {
    font-size: 14px !important;
  }

  .select-ios {
    background-position: right 12px center !important;
    background-size: 14px !important;
    padding-right: 36px !important;
  }

  /* Adjust label spacing */
  label {
    margin-bottom: 6px !important;
  }

  /* Make buttons more touch-friendly */
  button {
    min-height: 44px !important;
    padding: 10px 16px !important;
  }

  /* Adjust spacing between exam elements */
  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 12px !important;
  }

  .gap-3 {
    gap: 12px !important;
  }
}

/* Base border radius styles */
.min-h-screen .rounded-lg {
  border-radius: 8px !important;
}

.min-h-screen .rounded-xl {
  border-radius: 12px !important;
}

.min-h-screen .rounded-2xl {
  border-radius: 16px !important;
}

/* Input and select specific styles */
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  border-radius: 8px !important;
}

/* Button specific styles */
.min-h-screen button {
  border-radius: 8px !important;
}

/* Card specific styles */
.min-h-screen .bg-white {
  border-radius: 16px !important;
}

/* Modal specific styles */
:deep(.program-details-modal) {
  --border-radius: 16px 16px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 16px !important;
}

/* Segment specific styles */
:deep(.custom-segment) {
  --border-radius: 8px !important;
}

:deep(.custom-segment ion-segment-button) {
  border-radius: 8px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl {
    border-radius: 12px !important;
  }

  .min-h-screen .rounded-2xl {
    border-radius: 14px !important;
  }

  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }
}

/* Exam entry styles */
.bg-gray-50\/50 {
  background-color: rgba(249, 250, 251, 0.5) !important;
  backdrop-filter: blur(8px) !important;
}

.border-gray-100 {
  border-color: rgba(243, 244, 246, 0.8) !important;
}

/* Add hover effect to exam entries */
.bg-gray-50\/50:hover {
  background-color: rgba(249, 250, 251, 0.7) !important;
  border-color: rgba(229, 231, 235, 0.8) !important;
  transition: all 0.2s ease-in-out;
}

/* Compact exam entry layout */
@media (min-width: 640px) {
  .exam-entry-row {
    flex-direction: row !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 0 !important;
  }
}
@media (max-width: 639px) {
  .exam-entry-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 8px !important;
  }
}

/* Special border radius class with !important */
.force-radius {
  border-radius: 17px !important;
}

.force-radius-sm {
  border-radius: 12px !important;
}

/* Apply to all elements that need border radius */
.min-h-screen .rounded-lg,
.min-h-screen .rounded-xl,
.min-h-screen .rounded-2xl,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
.min-h-screen .bg-white,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80 {
  border-radius: 17px !important;
}

/* Smaller radius for some elements */
.min-h-screen .rounded-lg,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 12px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl,
  .min-h-screen .rounded-2xl,
  :deep(.program-details-modal) {
    border-radius: 14px !important;
  }
}

/* Force all text to be 16px */
.min-h-screen,
.min-h-screen *,
:deep(.program-details-modal),
:deep(.program-details-modal *) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Specific adjustments for headings */
.min-h-screen h1 {
  font-size: 28px !important;
  line-height: 1.2 !important;
}

.min-h-screen h2 {
  font-size: 24px !important;
  line-height: 1.3 !important;
}

.min-h-screen h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

/* Input and select specific text size */
.min-h-screen input,
.min-h-screen select,
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Button text size */
.min-h-screen button {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Label text size */
.min-h-screen label {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Modal specific text sizes */
:deep(.program-details-modal) h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

:deep(.program-details-modal) p {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-xs {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-sm {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-base {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Segment button text size */
:deep(.custom-segment ion-segment-button) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 24px !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
  }

  .min-h-screen input,
  .min-h-screen select,
  .min-h-screen .input-ios,
  .min-h-screen .select-ios,
  .min-h-screen button,
  .min-h-screen label {
    font-size: 16px !important;
  }
}

/* Ensure full width for all sections */
.min-h-screen > div {
  width: 100% !important;
  max-width: 100% !important;
}

/* Adjust grid layout for full width */
.lg\:grid {
  width: 100% !important;
  max-width: 100% !important;
}

/* Ensure content sections take full width */
.lg\:col-span-2,
.lg\:col-span-1 {
  width: 100% !important;
  max-width: 100% !important;
}

/* Custom card styles with !important */
.content-card {
  padding: 20px !important;
  margin: 0 8px 24px 8px !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 12px 32px 12px !important;
  }
}

.header-section {
  margin-bottom: 24px !important;
  padding: 0 16px !important;
  text-align: center !important;
}

@media (min-width: 640px) {
  .header-section {
    margin-bottom: 48px !important;
    padding: 0 24px !important;
  }
}

@media (min-width: 1024px) {
  .header-section {
    text-align: left !important;
  }
}

.exam-selection-card {
  background-color: white !important;
}

.info-panel-card,
.fun-facts-card {
  background-color: rgba(243, 244, 246, 0.8) !important;
  backdrop-filter: blur(8px) !important;
}

/* Ensure content spacing within cards */
.content-card > *:not(:last-child) {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card > *:not(:last-child) {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for nested elements */
.content-card h2,
.content-card h3 {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card h2,
  .content-card h3 {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for lists and other elements */
.content-card ul,
.content-card ol {
  margin: 12px 0 !important;
  padding-left: 24px !important;
}

.content-card li {
  margin-bottom: 8px !important;
}

/* Ensure proper spacing for buttons and interactive elements */
.content-card button {
  margin: 8px 0 !important;
}

/* Ensure proper spacing for form elements */
.content-card input,
.content-card select {
  margin-bottom: 12px !important;
}

/* Ensure proper spacing for the last element in cards */
.content-card > *:last-child {
  margin-bottom: 0 !important;
}

/* Ensure consistent grid layout */
@media (min-width: 1024px) {
  .lg\:grid {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 30px !important;
    padding: 0 15px !important;
    box-sizing: border-box !important;
  }

  .lg\:col-span-2 {
    grid-column: span 2 / span 2 !important;
  }
}

/* Adjust content card margins to align with grid */
.content-card {
  padding: 20px !important;
  margin: 0 0 24px 0 !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 0 32px 0 !important;
  }
}

/* Ensure the container takes full width */
.w-full {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* Custom border radius for all elements */
.content-card,
.header-section,
.exam-selection-card,
.info-panel-card,
.fun-facts-card,
.input-ios,
.select-ios,
button,
.rounded-lg,
.rounded-xl,
.rounded-2xl,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80,
.rounded-full {
  border-radius: 20px !important;
}

/* Specific adjustments for smaller elements */
.input-ios,
.select-ios,
button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 20px !important;
}

/* Ensure consistent border radius in modals */
:deep(.program-details-modal) {
  --border-radius: 20px 20px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 20px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .content-card,
  .header-section,
  .exam-selection-card,
  .info-panel-card,
  .fun-facts-card,
  :deep(.program-details-modal) {
    border-radius: 20px !important;
  }
}

/* Override any Tailwind border radius classes */
.rounded-lg,
.rounded-xl,
.rounded-2xl,
.rounded-full {
  border-radius: 20px !important;
}

/* Ensure buttons and inputs maintain the border radius */
button,
input,
select,
.input-ios,
.select-ios {
  border-radius: 20px !important;
}

/* Segment Container for Scrolling */
.segment-container {
  width: 100% !important;
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
  padding-bottom: 4px !important;
  position: relative !important;
}

.segment-container::-webkit-scrollbar {
  display: none !important;
}

/* Add shadow to indicate scrollable content */
.segment-container::after {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 40px !important;
  background: linear-gradient(to right, 
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 1) 100%
  ) !important;
  pointer-events: none !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  z-index: 10 !important;
}

.segment-container.has-content::after {
  opacity: 1 !important;
}

:deep(.custom-segment) {
  display: inline-flex !important;
  min-width: min-content !important;
  width: auto !important;
  overflow: visible !important;
  padding-right: 16px !important; /* Add padding to the right of the segment */
}

:deep(.custom-segment ion-segment-button) {
  white-space: nowrap !important;
}

@media (max-width: 640px) {
  .segment-container {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }

  :deep(.custom-segment) {
    padding-right: 8px !important; /* Reduce right padding on mobile */
  }
}

/* Custom Campus Segment */
.campus-segment-container {
  display: block !important;
  overflow-x: auto !important;
  white-space: nowrap !important;
  padding: 4px !important;
  background-color: rgba(235, 235, 245, 0.8) !important;
  border-radius: 12px !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important; /* Firefox */
}
.campus-segment-container::-webkit-scrollbar {
  display: none !important; /* Safari and Chrome */
}

.campus-segment-button {
  display: inline-block !important;
  padding: 8px 16px !important;
  margin: 0 2px !important;
  border: none !important;
  background-color: transparent !important;
  color: rgb(60, 60, 67, 0.6) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  border-radius: 9px !important;
  cursor: pointer !important;
  transition: all 0.2s ease-in-out !important;
}

.campus-segment-button.is-active {
  background-color: white !important;
  color: rgb(0, 122, 255) !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

/* Mobile-specific adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 28px !important;
    line-height: 1.2 !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
    line-height: 1.3 !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen p {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  .min-h-screen label {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen button {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen input,
  .min-h-screen select {
    font-size: 14px !important;
    line-height: 1.4 !important;
    padding: 8px !important;
  }

  /* Adjust card spacing and padding */
  .rounded-xl {
    border-radius: 12px !important;
  }

  .p-4 {
    padding: 16px !important;
  }

  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 16px !important;
  }

  /* Adjust grid gap */
  .gap-3 {
    gap: 12px !important;
  }

  /* Adjust icon sizes */
  .w-5 {
    width: 20px !important;
  }

  .h-5 {
    height: 20px !important;
  }

  .w-6 {
    width: 24px !important;
  }

  .h-6 {
    height: 24px !important;
  }
}

/* Adjust modal styles for mobile */
@media (max-width: 640px) {
  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }

  :deep(.program-details-modal ion-header) {
    padding: 12px;
  }

  :deep(.program-details-modal ion-content) {
    --padding-top: 16px;
    --padding-bottom: 16px;
    --padding-start: 16px;
    --padding-end: 16px;
  }

  :deep(.program-details-modal h3) {
    font-size: 19px !important;
  }

  :deep(.program-details-modal p) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-xs) {
    font-size: 13px !important;
  }

  :deep(.program-details-modal .text-sm) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-base) {
    font-size: 17px !important;
  }
}

/* Adjust segment styles for mobile */
@media (max-width: 640px) {
  :deep(.custom-segment) {
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 4px;
    --padding-end: 4px;
  }

  :deep(.custom-segment ion-segment-button) {
    font-size: 14px !important;
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 8px;
    --padding-end: 8px;
  }
}

/* Mobile-specific input styles */
@media (max-width: 640px) {
  .input-ios,
  .select-ios {
    padding: 10px 14px !important;
    min-height: 44px !important;
    font-size: 14px !important;
  }

  .input-ios::placeholder {
    font-size: 14px !important;
  }

  .select-ios {
    background-position: right 12px center !important;
    background-size: 14px !important;
    padding-right: 36px !important;
  }

  /* Adjust label spacing */
  label {
    margin-bottom: 6px !important;
  }

  /* Make buttons more touch-friendly */
  button {
    min-height: 44px !important;
    padding: 10px 16px !important;
  }

  /* Adjust spacing between exam elements */
  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 12px !important;
  }

  .gap-3 {
    gap: 12px !important;
  }
}

/* Base border radius styles */
.min-h-screen .rounded-lg {
  border-radius: 8px !important;
}

.min-h-screen .rounded-xl {
  border-radius: 12px !important;
}

.min-h-screen .rounded-2xl {
  border-radius: 16px !important;
}

/* Input and select specific styles */
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  border-radius: 8px !important;
}

/* Button specific styles */
.min-h-screen button {
  border-radius: 8px !important;
}

/* Card specific styles */
.min-h-screen .bg-white {
  border-radius: 16px !important;
}

/* Modal specific styles */
:deep(.program-details-modal) {
  --border-radius: 16px 16px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 16px !important;
}

/* Segment specific styles */
:deep(.custom-segment) {
  --border-radius: 8px !important;
}

:deep(.custom-segment ion-segment-button) {
  border-radius: 8px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl {
    border-radius: 12px !important;
  }

  .min-h-screen .rounded-2xl {
    border-radius: 14px !important;
  }

  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }
}

/* Exam entry styles */
.bg-gray-50\/50 {
  background-color: rgba(249, 250, 251, 0.5) !important;
  backdrop-filter: blur(8px) !important;
}

.border-gray-100 {
  border-color: rgba(243, 244, 246, 0.8) !important;
}

/* Add hover effect to exam entries */
.bg-gray-50\/50:hover {
  background-color: rgba(249, 250, 251, 0.7) !important;
  border-color: rgba(229, 231, 235, 0.8) !important;
  transition: all 0.2s ease-in-out;
}

/* Compact exam entry layout */
@media (min-width: 640px) {
  .exam-entry-row {
    flex-direction: row !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 0 !important;
  }
}
@media (max-width: 639px) {
  .exam-entry-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 8px !important;
  }
}

/* Special border radius class with !important */
.force-radius {
  border-radius: 17px !important;
}

.force-radius-sm {
  border-radius: 12px !important;
}

/* Apply to all elements that need border radius */
.min-h-screen .rounded-lg,
.min-h-screen .rounded-xl,
.min-h-screen .rounded-2xl,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
.min-h-screen .bg-white,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80 {
  border-radius: 17px !important;
}

/* Smaller radius for some elements */
.min-h-screen .rounded-lg,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 12px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl,
  .min-h-screen .rounded-2xl,
  :deep(.program-details-modal) {
    border-radius: 14px !important;
  }
}

/* Force all text to be 16px */
.min-h-screen,
.min-h-screen *,
:deep(.program-details-modal),
:deep(.program-details-modal *) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Specific adjustments for headings */
.min-h-screen h1 {
  font-size: 28px !important;
  line-height: 1.2 !important;
}

.min-h-screen h2 {
  font-size: 24px !important;
  line-height: 1.3 !important;
}

.min-h-screen h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

/* Input and select specific text size */
.min-h-screen input,
.min-h-screen select,
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Button text size */
.min-h-screen button {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Label text size */
.min-h-screen label {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Modal specific text sizes */
:deep(.program-details-modal) h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

:deep(.program-details-modal) p {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-xs {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-sm {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-base {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Segment button text size */
:deep(.custom-segment ion-segment-button) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 24px !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
  }

  .min-h-screen input,
  .min-h-screen select,
  .min-h-screen .input-ios,
  .min-h-screen .select-ios,
  .min-h-screen button,
  .min-h-screen label {
    font-size: 16px !important;
  }
}

/* Ensure full width for all sections */
.min-h-screen > div {
  width: 100% !important;
  max-width: 100% !important;
}

/* Adjust grid layout for full width */
.lg\:grid {
  width: 100% !important;
  max-width: 100% !important;
}

/* Ensure content sections take full width */
.lg\:col-span-2,
.lg\:col-span-1 {
  width: 100% !important;
  max-width: 100% !important;
}

/* Custom card styles with !important */
.content-card {
  padding: 20px !important;
  margin: 0 8px 24px 8px !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 12px 32px 12px !important;
  }
}

.header-section {
  margin-bottom: 24px !important;
  padding: 0 16px !important;
  text-align: center !important;
}

@media (min-width: 640px) {
  .header-section {
    margin-bottom: 48px !important;
    padding: 0 24px !important;
  }
}

@media (min-width: 1024px) {
  .header-section {
    text-align: left !important;
  }
}

.exam-selection-card {
  background-color: white !important;
}

.info-panel-card,
.fun-facts-card {
  background-color: rgba(243, 244, 246, 0.8) !important;
  backdrop-filter: blur(8px) !important;
}

/* Ensure content spacing within cards */
.content-card > *:not(:last-child) {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card > *:not(:last-child) {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for nested elements */
.content-card h2,
.content-card h3 {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card h2,
  .content-card h3 {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for lists and other elements */
.content-card ul,
.content-card ol {
  margin: 12px 0 !important;
  padding-left: 24px !important;
}

.content-card li {
  margin-bottom: 8px !important;
}

/* Ensure proper spacing for buttons and interactive elements */
.content-card button {
  margin: 8px 0 !important;
}

/* Ensure proper spacing for form elements */
.content-card input,
.content-card select {
  margin-bottom: 12px !important;
}

/* Ensure proper spacing for the last element in cards */
.content-card > *:last-child {
  margin-bottom: 0 !important;
}

/* Ensure consistent grid layout */
@media (min-width: 1024px) {
  .lg\:grid {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 30px !important;
    padding: 0 15px !important;
    box-sizing: border-box !important;
  }

  .lg\:col-span-2 {
    grid-column: span 2 / span 2 !important;
  }
}

/* Adjust content card margins to align with grid */
.content-card {
  padding: 20px !important;
  margin: 0 0 24px 0 !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 0 32px 0 !important;
  }
}

/* Ensure the container takes full width */
.w-full {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* Custom border radius for all elements */
.content-card,
.header-section,
.exam-selection-card,
.info-panel-card,
.fun-facts-card,
.input-ios,
.select-ios,
button,
.rounded-lg,
.rounded-xl,
.rounded-2xl,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80,
.rounded-full {
  border-radius: 20px !important;
}

/* Specific adjustments for smaller elements */
.input-ios,
.select-ios,
button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 20px !important;
}

/* Ensure consistent border radius in modals */
:deep(.program-details-modal) {
  --border-radius: 20px 20px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 20px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .content-card,
  .header-section,
  .exam-selection-card,
  .info-panel-card,
  .fun-facts-card,
  :deep(.program-details-modal) {
    border-radius: 20px !important;
  }
}

/* Override any Tailwind border radius classes */
.rounded-lg,
.rounded-xl,
.rounded-2xl,
.rounded-full {
  border-radius: 20px !important;
}

/* Ensure buttons and inputs maintain the border radius */
button,
input,
select,
.input-ios,
.select-ios {
  border-radius: 20px !important;
}

/* Segment Container for Scrolling */
.segment-container {
  width: 100% !important;
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
  padding-bottom: 4px !important;
  position: relative !important;
}

.segment-container::-webkit-scrollbar {
  display: none !important;
}

/* Add shadow to indicate scrollable content */
.segment-container::after {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 40px !important;
  background: linear-gradient(to right, 
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 1) 100%
  ) !important;
  pointer-events: none !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  z-index: 10 !important;
}

.segment-container.has-content::after {
  opacity: 1 !important;
}

:deep(.custom-segment) {
  display: inline-flex !important;
  min-width: min-content !important;
  width: auto !important;
  overflow: visible !important;
  padding-right: 16px !important; /* Add padding to the right of the segment */
}

:deep(.custom-segment ion-segment-button) {
  white-space: nowrap !important;
}

@media (max-width: 640px) {
  .segment-container {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }

  :deep(.custom-segment) {
    padding-right: 8px !important; /* Reduce right padding on mobile */
  }
}

/* Custom Campus Segment */
.campus-segment-container {
  display: block !important;
  overflow-x: auto !important;
  white-space: nowrap !important;
  padding: 4px !important;
  background-color: rgba(235, 235, 245, 0.8) !important;
  border-radius: 12px !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important; /* Firefox */
}
.campus-segment-container::-webkit-scrollbar {
  display: none !important; /* Safari and Chrome */
}

.campus-segment-button {
  display: inline-block !important;
  padding: 8px 16px !important;
  margin: 0 2px !important;
  border: none !important;
  background-color: transparent !important;
  color: rgb(60, 60, 67, 0.6) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  border-radius: 9px !important;
  cursor: pointer !important;
  transition: all 0.2s ease-in-out !important;
}

.campus-segment-button.is-active {
  background-color: white !important;
  color: rgb(0, 122, 255) !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

/* Mobile-specific adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 28px !important;
    line-height: 1.2 !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
    line-height: 1.3 !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen p {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  .min-h-screen label {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen button {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen input,
  .min-h-screen select {
    font-size: 14px !important;
    line-height: 1.4 !important;
    padding: 8px !important;
  }

  /* Adjust card spacing and padding */
  .rounded-xl {
    border-radius: 12px !important;
  }

  .p-4 {
    padding: 16px !important;
  }

  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 16px !important;
  }

  /* Adjust grid gap */
  .gap-3 {
    gap: 12px !important;
  }

  /* Adjust icon sizes */
  .w-5 {
    width: 20px !important;
  }

  .h-5 {
    height: 20px !important;
  }

  .w-6 {
    width: 24px !important;
  }

  .h-6 {
    height: 24px !important;
  }
}

/* Adjust modal styles for mobile */
@media (max-width: 640px) {
  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }

  :deep(.program-details-modal ion-header) {
    padding: 12px;
  }

  :deep(.program-details-modal ion-content) {
    --padding-top: 16px;
    --padding-bottom: 16px;
    --padding-start: 16px;
    --padding-end: 16px;
  }

  :deep(.program-details-modal h3) {
    font-size: 19px !important;
  }

  :deep(.program-details-modal p) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-xs) {
    font-size: 13px !important;
  }

  :deep(.program-details-modal .text-sm) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-base) {
    font-size: 17px !important;
  }
}

/* Adjust segment styles for mobile */
@media (max-width: 640px) {
  :deep(.custom-segment) {
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 4px;
    --padding-end: 4px;
  }

  :deep(.custom-segment ion-segment-button) {
    font-size: 14px !important;
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 8px;
    --padding-end: 8px;
  }
}

/* Mobile-specific input styles */
@media (max-width: 640px) {
  .input-ios,
  .select-ios {
    padding: 10px 14px !important;
    min-height: 44px !important;
    font-size: 14px !important;
  }

  .input-ios::placeholder {
    font-size: 14px !important;
  }

  .select-ios {
    background-position: right 12px center !important;
    background-size: 14px !important;
    padding-right: 36px !important;
  }

  /* Adjust label spacing */
  label {
    margin-bottom: 6px !important;
  }

  /* Make buttons more touch-friendly */
  button {
    min-height: 44px !important;
    padding: 10px 16px !important;
  }

  /* Adjust spacing between exam elements */
  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 12px !important;
  }

  .gap-3 {
    gap: 12px !important;
  }
}

/* Base border radius styles */
.min-h-screen .rounded-lg {
  border-radius: 8px !important;
}

.min-h-screen .rounded-xl {
  border-radius: 12px !important;
}

.min-h-screen .rounded-2xl {
  border-radius: 16px !important;
}

/* Input and select specific styles */
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  border-radius: 8px !important;
}

/* Button specific styles */
.min-h-screen button {
  border-radius: 8px !important;
}

/* Card specific styles */
.min-h-screen .bg-white {
  border-radius: 16px !important;
}

/* Modal specific styles */
:deep(.program-details-modal) {
  --border-radius: 16px 16px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 16px !important;
}

/* Segment specific styles */
:deep(.custom-segment) {
  --border-radius: 8px !important;
}

:deep(.custom-segment ion-segment-button) {
  border-radius: 8px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl {
    border-radius: 12px !important;
  }

  .min-h-screen .rounded-2xl {
    border-radius: 14px !important;
  }

  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }
}

/* Exam entry styles */
.bg-gray-50\/50 {
  background-color: rgba(249, 250, 251, 0.5) !important;
  backdrop-filter: blur(8px) !important;
}

.border-gray-100 {
  border-color: rgba(243, 244, 246, 0.8) !important;
}

/* Add hover effect to exam entries */
.bg-gray-50\/50:hover {
  background-color: rgba(249, 250, 251, 0.7) !important;
  border-color: rgba(229, 231, 235, 0.8) !important;
  transition: all 0.2s ease-in-out;
}

/* Compact exam entry layout */
@media (min-width: 640px) {
  .exam-entry-row {
    flex-direction: row !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 0 !important;
  }
}
@media (max-width: 639px) {
  .exam-entry-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 8px !important;
  }
}

/* Special border radius class with !important */
.force-radius {
  border-radius: 17px !important;
}

.force-radius-sm {
  border-radius: 12px !important;
}

/* Apply to all elements that need border radius */
.min-h-screen .rounded-lg,
.min-h-screen .rounded-xl,
.min-h-screen .rounded-2xl,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
.min-h-screen .bg-white,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80 {
  border-radius: 17px !important;
}

/* Smaller radius for some elements */
.min-h-screen .rounded-lg,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 12px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl,
  .min-h-screen .rounded-2xl,
  :deep(.program-details-modal) {
    border-radius: 14px !important;
  }
}

/* Force all text to be 16px */
.min-h-screen,
.min-h-screen *,
:deep(.program-details-modal),
:deep(.program-details-modal *) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Specific adjustments for headings */
.min-h-screen h1 {
  font-size: 28px !important;
  line-height: 1.2 !important;
}

.min-h-screen h2 {
  font-size: 24px !important;
  line-height: 1.3 !important;
}

.min-h-screen h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

/* Input and select specific text size */
.min-h-screen input,
.min-h-screen select,
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Button text size */
.min-h-screen button {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Label text size */
.min-h-screen label {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Modal specific text sizes */
:deep(.program-details-modal) h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

:deep(.program-details-modal) p {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-xs {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-sm {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-base {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Segment button text size */
:deep(.custom-segment ion-segment-button) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 24px !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
  }

  .min-h-screen input,
  .min-h-screen select,
  .min-h-screen .input-ios,
  .min-h-screen .select-ios,
  .min-h-screen button,
  .min-h-screen label {
    font-size: 16px !important;
  }
}

/* Ensure full width for all sections */
.min-h-screen > div {
  width: 100% !important;
  max-width: 100% !important;
}

/* Adjust grid layout for full width */
.lg\:grid {
  width: 100% !important;
  max-width: 100% !important;
}

/* Ensure content sections take full width */
.lg\:col-span-2,
.lg\:col-span-1 {
  width: 100% !important;
  max-width: 100% !important;
}

/* Custom card styles with !important */
.content-card {
  padding: 20px !important;
  margin: 0 8px 24px 8px !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 12px 32px 12px !important;
  }
}

.header-section {
  margin-bottom: 24px !important;
  padding: 0 16px !important;
  text-align: center !important;
}

@media (min-width: 640px) {
  .header-section {
    margin-bottom: 48px !important;
    padding: 0 24px !important;
  }
}

@media (min-width: 1024px) {
  .header-section {
    text-align: left !important;
  }
}

.exam-selection-card {
  background-color: white !important;
}

.info-panel-card,
.fun-facts-card {
  background-color: rgba(243, 244, 246, 0.8) !important;
  backdrop-filter: blur(8px) !important;
}

/* Ensure content spacing within cards */
.content-card > *:not(:last-child) {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card > *:not(:last-child) {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for nested elements */
.content-card h2,
.content-card h3 {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card h2,
  .content-card h3 {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for lists and other elements */
.content-card ul,
.content-card ol {
  margin: 12px 0 !important;
  padding-left: 24px !important;
}

.content-card li {
  margin-bottom: 8px !important;
}

/* Ensure proper spacing for buttons and interactive elements */
.content-card button {
  margin: 8px 0 !important;
}

/* Ensure proper spacing for form elements */
.content-card input,
.content-card select {
  margin-bottom: 12px !important;
}

/* Ensure proper spacing for the last element in cards */
.content-card > *:last-child {
  margin-bottom: 0 !important;
}

/* Ensure consistent grid layout */
@media (min-width: 1024px) {
  .lg\:grid {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 30px !important;
    padding: 0 15px !important;
    box-sizing: border-box !important;
  }

  .lg\:col-span-2 {
    grid-column: span 2 / span 2 !important;
  }
}

/* Adjust content card margins to align with grid */
.content-card {
  padding: 20px !important;
  margin: 0 0 24px 0 !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 0 32px 0 !important;
  }
}

/* Ensure the container takes full width */
.w-full {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* Custom border radius for all elements */
.content-card,
.header-section,
.exam-selection-card,
.info-panel-card,
.fun-facts-card,
.input-ios,
.select-ios,
button,
.rounded-lg,
.rounded-xl,
.rounded-2xl,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80,
.rounded-full {
  border-radius: 20px !important;
}

/* Specific adjustments for smaller elements */
.input-ios,
.select-ios,
button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 20px !important;
}

/* Ensure consistent border radius in modals */
:deep(.program-details-modal) {
  --border-radius: 20px 20px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 20px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .content-card,
  .header-section,
  .exam-selection-card,
  .info-panel-card,
  .fun-facts-card,
  :deep(.program-details-modal) {
    border-radius: 20px !important;
  }
}

/* Override any Tailwind border radius classes */
.rounded-lg,
.rounded-xl,
.rounded-2xl,
.rounded-full {
  border-radius: 20px !important;
}

/* Ensure buttons and inputs maintain the border radius */
button,
input,
select,
.input-ios,
.select-ios {
  border-radius: 20px !important;
}

/* Segment Container for Scrolling */
.segment-container {
  width: 100% !important;
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
  padding-bottom: 4px !important;
  position: relative !important;
}

.segment-container::-webkit-scrollbar {
  display: none !important;
}

/* Add shadow to indicate scrollable content */
.segment-container::after {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 40px !important;
  background: linear-gradient(to right, 
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 1) 100%
  ) !important;
  pointer-events: none !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  z-index: 10 !important;
}

.segment-container.has-content::after {
  opacity: 1 !important;
}

:deep(.custom-segment) {
  display: inline-flex !important;
  min-width: min-content !important;
  width: auto !important;
  overflow: visible !important;
  padding-right: 16px !important; /* Add padding to the right of the segment */
}

:deep(.custom-segment ion-segment-button) {
  white-space: nowrap !important;
}

@media (max-width: 640px) {
  .segment-container {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }

  :deep(.custom-segment) {
    padding-right: 8px !important; /* Reduce right padding on mobile */
  }
}

/* Custom Campus Segment */
.campus-segment-container {
  display: block !important;
  overflow-x: auto !important;
  white-space: nowrap !important;
  padding: 4px !important;
  background-color: rgba(235, 235, 245, 0.8) !important;
  border-radius: 12px !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important; /* Firefox */
}
.campus-segment-container::-webkit-scrollbar {
  display: none !important; /* Safari and Chrome */
}

.campus-segment-button {
  display: inline-block !important;
  padding: 8px 16px !important;
  margin: 0 2px !important;
  border: none !important;
  background-color: transparent !important;
  color: rgb(60, 60, 67, 0.6) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  border-radius: 9px !important;
  cursor: pointer !important;
  transition: all 0.2s ease-in-out !important;
}

.campus-segment-button.is-active {
  background-color: white !important;
  color: rgb(0, 122, 255) !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

/* Mobile-specific adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 28px !important;
    line-height: 1.2 !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
    line-height: 1.3 !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen p {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  .min-h-screen label {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen button {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen input,
  .min-h-screen select {
    font-size: 14px !important;
    line-height: 1.4 !important;
    padding: 8px !important;
  }

  /* Adjust card spacing and padding */
  .rounded-xl {
    border-radius: 12px !important;
  }

  .p-4 {
    padding: 16px !important;
  }

  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 16px !important;
  }

  /* Adjust grid gap */
  .gap-3 {
    gap: 12px !important;
  }

  /* Adjust icon sizes */
  .w-5 {
    width: 20px !important;
  }

  .h-5 {
    height: 20px !important;
  }

  .w-6 {
    width: 24px !important;
  }

  .h-6 {
    height: 24px !important;
  }
}

/* Adjust modal styles for mobile */
@media (max-width: 640px) {
  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }

  :deep(.program-details-modal ion-header) {
    padding: 12px;
  }

  :deep(.program-details-modal ion-content) {
    --padding-top: 16px;
    --padding-bottom: 16px;
    --padding-start: 16px;
    --padding-end: 16px;
  }

  :deep(.program-details-modal h3) {
    font-size: 19px !important;
  }

  :deep(.program-details-modal p) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-xs) {
    font-size: 13px !important;
  }

  :deep(.program-details-modal .text-sm) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-base) {
    font-size: 17px !important;
  }
}

/* Adjust segment styles for mobile */
@media (max-width: 640px) {
  :deep(.custom-segment) {
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 4px;
    --padding-end: 4px;
  }

  :deep(.custom-segment ion-segment-button) {
    font-size: 14px !important;
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 8px;
    --padding-end: 8px;
  }
}

/* Mobile-specific input styles */
@media (max-width: 640px) {
  .input-ios,
  .select-ios {
    padding: 10px 14px !important;
    min-height: 44px !important;
    font-size: 14px !important;
  }

  .input-ios::placeholder {
    font-size: 14px !important;
  }

  .select-ios {
    background-position: right 12px center !important;
    background-size: 14px !important;
    padding-right: 36px !important;
  }

  /* Adjust label spacing */
  label {
    margin-bottom: 6px !important;
  }

  /* Make buttons more touch-friendly */
  button {
    min-height: 44px !important;
    padding: 10px 16px !important;
  }

  /* Adjust spacing between exam elements */
  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 12px !important;
  }

  .gap-3 {
    gap: 12px !important;
  }
}

/* Base border radius styles */
.min-h-screen .rounded-lg {
  border-radius: 8px !important;
}

.min-h-screen .rounded-xl {
  border-radius: 12px !important;
}

.min-h-screen .rounded-2xl {
  border-radius: 16px !important;
}

/* Input and select specific styles */
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  border-radius: 8px !important;
}

/* Button specific styles */
.min-h-screen button {
  border-radius: 8px !important;
}

/* Card specific styles */
.min-h-screen .bg-white {
  border-radius: 16px !important;
}

/* Modal specific styles */
:deep(.program-details-modal) {
  --border-radius: 16px 16px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 16px !important;
}

/* Segment specific styles */
:deep(.custom-segment) {
  --border-radius: 8px !important;
}

:deep(.custom-segment ion-segment-button) {
  border-radius: 8px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl {
    border-radius: 12px !important;
  }

  .min-h-screen .rounded-2xl {
    border-radius: 14px !important;
  }

  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }
}

/* Exam entry styles */
.bg-gray-50\/50 {
  background-color: rgba(249, 250, 251, 0.5) !important;
  backdrop-filter: blur(8px) !important;
}

.border-gray-100 {
  border-color: rgba(243, 244, 246, 0.8) !important;
}

/* Add hover effect to exam entries */
.bg-gray-50\/50:hover {
  background-color: rgba(249, 250, 251, 0.7) !important;
  border-color: rgba(229, 231, 235, 0.8) !important;
  transition: all 0.2s ease-in-out;
}

/* Compact exam entry layout */
@media (min-width: 640px) {
  .exam-entry-row {
    flex-direction: row !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 0 !important;
  }
}
@media (max-width: 639px) {
  .exam-entry-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 8px !important;
  }
}

/* Special border radius class with !important */
.force-radius {
  border-radius: 17px !important;
}

.force-radius-sm {
  border-radius: 12px !important;
}

/* Apply to all elements that need border radius */
.min-h-screen .rounded-lg,
.min-h-screen .rounded-xl,
.min-h-screen .rounded-2xl,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
.min-h-screen .bg-white,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80 {
  border-radius: 17px !important;
}

/* Smaller radius for some elements */
.min-h-screen .rounded-lg,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 12px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl,
  .min-h-screen .rounded-2xl,
  :deep(.program-details-modal) {
    border-radius: 14px !important;
  }
}

/* Force all text to be 16px */
.min-h-screen,
.min-h-screen *,
:deep(.program-details-modal),
:deep(.program-details-modal *) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Specific adjustments for headings */
.min-h-screen h1 {
  font-size: 28px !important;
  line-height: 1.2 !important;
}

.min-h-screen h2 {
  font-size: 24px !important;
  line-height: 1.3 !important;
}

.min-h-screen h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

/* Input and select specific text size */
.min-h-screen input,
.min-h-screen select,
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Button text size */
.min-h-screen button {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Label text size */
.min-h-screen label {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Modal specific text sizes */
:deep(.program-details-modal) h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

:deep(.program-details-modal) p {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-xs {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-sm {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-base {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Segment button text size */
:deep(.custom-segment ion-segment-button) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 24px !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
  }

  .min-h-screen input,
  .min-h-screen select,
  .min-h-screen .input-ios,
  .min-h-screen .select-ios,
  .min-h-screen button,
  .min-h-screen label {
    font-size: 16px !important;
  }
}

/* Ensure full width for all sections */
.min-h-screen > div {
  width: 100% !important;
  max-width: 100% !important;
}

/* Adjust grid layout for full width */
.lg\:grid {
  width: 100% !important;
  max-width: 100% !important;
}

/* Ensure content sections take full width */
.lg\:col-span-2,
.lg\:col-span-1 {
  width: 100% !important;
  max-width: 100% !important;
}

/* Custom card styles with !important */
.content-card {
  padding: 20px !important;
  margin: 0 8px 24px 8px !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 12px 32px 12px !important;
  }
}

.header-section {
  margin-bottom: 24px !important;
  padding: 0 16px !important;
  text-align: center !important;
}

@media (min-width: 640px) {
  .header-section {
    margin-bottom: 48px !important;
    padding: 0 24px !important;
  }
}

@media (min-width: 1024px) {
  .header-section {
    text-align: left !important;
  }
}

.exam-selection-card {
  background-color: white !important;
}

.info-panel-card,
.fun-facts-card {
  background-color: rgba(243, 244, 246, 0.8) !important;
  backdrop-filter: blur(8px) !important;
}

/* Ensure content spacing within cards */
.content-card > *:not(:last-child) {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card > *:not(:last-child) {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for nested elements */
.content-card h2,
.content-card h3 {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card h2,
  .content-card h3 {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for lists and other elements */
.content-card ul,
.content-card ol {
  margin: 12px 0 !important;
  padding-left: 24px !important;
}

.content-card li {
  margin-bottom: 8px !important;
}

/* Ensure proper spacing for buttons and interactive elements */
.content-card button {
  margin: 8px 0 !important;
}

/* Ensure proper spacing for form elements */
.content-card input,
.content-card select {
  margin-bottom: 12px !important;
}

/* Ensure proper spacing for the last element in cards */
.content-card > *:last-child {
  margin-bottom: 0 !important;
}

/* Ensure consistent grid layout */
@media (min-width: 1024px) {
  .lg\:grid {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 30px !important;
    padding: 0 15px !important;
    box-sizing: border-box !important;
  }

  .lg\:col-span-2 {
    grid-column: span 2 / span 2 !important;
  }
}

/* Adjust content card margins to align with grid */
.content-card {
  padding: 20px !important;
  margin: 0 0 24px 0 !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 0 32px 0 !important;
  }
}

/* Ensure the container takes full width */
.w-full {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* Custom border radius for all elements */
.content-card,
.header-section,
.exam-selection-card,
.info-panel-card,
.fun-facts-card,
.input-ios,
.select-ios,
button,
.rounded-lg,
.rounded-xl,
.rounded-2xl,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80,
.rounded-full {
  border-radius: 20px !important;
}

/* Specific adjustments for smaller elements */
.input-ios,
.select-ios,
button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 20px !important;
}

/* Ensure consistent border radius in modals */
:deep(.program-details-modal) {
  --border-radius: 20px 20px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 20px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .content-card,
  .header-section,
  .exam-selection-card,
  .info-panel-card,
  .fun-facts-card,
  :deep(.program-details-modal) {
    border-radius: 20px !important;
  }
}

/* Override any Tailwind border radius classes */
.rounded-lg,
.rounded-xl,
.rounded-2xl,
.rounded-full {
  border-radius: 20px !important;
}

/* Ensure buttons and inputs maintain the border radius */
button,
input,
select,
.input-ios,
.select-ios {
  border-radius: 20px !important;
}

/* Segment Container for Scrolling */
.segment-container {
  width: 100% !important;
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
  padding-bottom: 4px !important;
  position: relative !important;
}

.segment-container::-webkit-scrollbar {
  display: none !important;
}

/* Add shadow to indicate scrollable content */
.segment-container::after {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 40px !important;
  background: linear-gradient(to right, 
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 1) 100%
  ) !important;
  pointer-events: none !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  z-index: 10 !important;
}

.segment-container.has-content::after {
  opacity: 1 !important;
}

:deep(.custom-segment) {
  display: inline-flex !important;
  min-width: min-content !important;
  width: auto !important;
  overflow: visible !important;
  padding-right: 16px !important; /* Add padding to the right of the segment */
}

:deep(.custom-segment ion-segment-button) {
  white-space: nowrap !important;
}

@media (max-width: 640px) {
  .segment-container {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }

  :deep(.custom-segment) {
    padding-right: 8px !important; /* Reduce right padding on mobile */
  }
}

/* Custom Campus Segment */
.campus-segment-container {
  display: block !important;
  overflow-x: auto !important;
  white-space: nowrap !important;
  padding: 4px !important;
  background-color: rgba(235, 235, 245, 0.8) !important;
  border-radius: 12px !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important; /* Firefox */
}
.campus-segment-container::-webkit-scrollbar {
  display: none !important; /* Safari and Chrome */
}

.campus-segment-button {
  display: inline-block !important;
  padding: 8px 16px !important;
  margin: 0 2px !important;
  border: none !important;
  background-color: transparent !important;
  color: rgb(60, 60, 67, 0.6) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  border-radius: 9px !important;
  cursor: pointer !important;
  transition: all 0.2s ease-in-out !important;
}

.campus-segment-button.is-active {
  background-color: white !important;
  color: rgb(0, 122, 255) !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

/* Mobile-specific adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 28px !important;
    line-height: 1.2 !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
    line-height: 1.3 !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen p {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  .min-h-screen label {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen button {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen input,
  .min-h-screen select {
    font-size: 14px !important;
    line-height: 1.4 !important;
    padding: 8px !important;
  }

  /* Adjust card spacing and padding */
  .rounded-xl {
    border-radius: 12px !important;
  }

  .p-4 {
    padding: 16px !important;
  }

  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 16px !important;
  }

  /* Adjust grid gap */
  .gap-3 {
    gap: 12px !important;
  }

  /* Adjust icon sizes */
  .w-5 {
    width: 20px !important;
  }

  .h-5 {
    height: 20px !important;
  }

  .w-6 {
    width: 24px !important;
  }

  .h-6 {
    height: 24px !important;
  }
}

/* Adjust modal styles for mobile */
@media (max-width: 640px) {
  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }

  :deep(.program-details-modal ion-header) {
    padding: 12px;
  }

  :deep(.program-details-modal ion-content) {
    --padding-top: 16px;
    --padding-bottom: 16px;
    --padding-start: 16px;
    --padding-end: 16px;
  }

  :deep(.program-details-modal h3) {
    font-size: 19px !important;
  }

  :deep(.program-details-modal p) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-xs) {
    font-size: 13px !important;
  }

  :deep(.program-details-modal .text-sm) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-base) {
    font-size: 17px !important;
  }
}

/* Adjust segment styles for mobile */
@media (max-width: 640px) {
  :deep(.custom-segment) {
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 4px;
    --padding-end: 4px;
  }

  :deep(.custom-segment ion-segment-button) {
    font-size: 14px !important;
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 8px;
    --padding-end: 8px;
  }
}

/* Mobile-specific input styles */
@media (max-width: 640px) {
  .input-ios,
  .select-ios {
    padding: 10px 14px !important;
    min-height: 44px !important;
    font-size: 14px !important;
  }

  .input-ios::placeholder {
    font-size: 14px !important;
  }

  .select-ios {
    background-position: right 12px center !important;
    background-size: 14px !important;
    padding-right: 36px !important;
  }

  /* Adjust label spacing */
  label {
    margin-bottom: 6px !important;
  }

  /* Make buttons more touch-friendly */
  button {
    min-height: 44px !important;
    padding: 10px 16px !important;
  }

  /* Adjust spacing between exam elements */
  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 12px !important;
  }

  .gap-3 {
    gap: 12px !important;
  }
}

/* Base border radius styles */
.min-h-screen .rounded-lg {
  border-radius: 8px !important;
}

.min-h-screen .rounded-xl {
  border-radius: 12px !important;
}

.min-h-screen .rounded-2xl {
  border-radius: 16px !important;
}

/* Input and select specific styles */
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  border-radius: 8px !important;
}

/* Button specific styles */
.min-h-screen button {
  border-radius: 8px !important;
}

/* Card specific styles */
.min-h-screen .bg-white {
  border-radius: 16px !important;
}

/* Modal specific styles */
:deep(.program-details-modal) {
  --border-radius: 16px 16px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 16px !important;
}

/* Segment specific styles */
:deep(.custom-segment) {
  --border-radius: 8px !important;
}

:deep(.custom-segment ion-segment-button) {
  border-radius: 8px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl {
    border-radius: 12px !important;
  }

  .min-h-screen .rounded-2xl {
    border-radius: 14px !important;
  }

  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }
}

/* Exam entry styles */
.bg-gray-50\/50 {
  background-color: rgba(249, 250, 251, 0.5) !important;
  backdrop-filter: blur(8px) !important;
}

.border-gray-100 {
  border-color: rgba(243, 244, 246, 0.8) !important;
}

/* Add hover effect to exam entries */
.bg-gray-50\/50:hover {
  background-color: rgba(249, 250, 251, 0.7) !important;
  border-color: rgba(229, 231, 235, 0.8) !important;
  transition: all 0.2s ease-in-out;
}

/* Compact exam entry layout */
@media (min-width: 640px) {
  .exam-entry-row {
    flex-direction: row !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 0 !important;
  }
}
@media (max-width: 639px) {
  .exam-entry-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 8px !important;
  }
}

/* Special border radius class with !important */
.force-radius {
  border-radius: 17px !important;
}

.force-radius-sm {
  border-radius: 12px !important;
}

/* Apply to all elements that need border radius */
.min-h-screen .rounded-lg,
.min-h-screen .rounded-xl,
.min-h-screen .rounded-2xl,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
.min-h-screen .bg-white,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80 {
  border-radius: 17px !important;
}

/* Smaller radius for some elements */
.min-h-screen .rounded-lg,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 12px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl,
  .min-h-screen .rounded-2xl,
  :deep(.program-details-modal) {
    border-radius: 14px !important;
  }
}

/* Force all text to be 16px */
.min-h-screen,
.min-h-screen *,
:deep(.program-details-modal),
:deep(.program-details-modal *) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Specific adjustments for headings */
.min-h-screen h1 {
  font-size: 28px !important;
  line-height: 1.2 !important;
}

.min-h-screen h2 {
  font-size: 24px !important;
  line-height: 1.3 !important;
}

.min-h-screen h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

/* Input and select specific text size */
.min-h-screen input,
.min-h-screen select,
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Button text size */
.min-h-screen button {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Label text size */
.min-h-screen label {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Modal specific text sizes */
:deep(.program-details-modal) h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

:deep(.program-details-modal) p {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-xs {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-sm {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-base {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Segment button text size */
:deep(.custom-segment ion-segment-button) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 24px !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
  }

  .min-h-screen input,
  .min-h-screen select,
  .min-h-screen .input-ios,
  .min-h-screen .select-ios,
  .min-h-screen button,
  .min-h-screen label {
    font-size: 16px !important;
  }
}

/* Ensure full width for all sections */
.min-h-screen > div {
  width: 100% !important;
  max-width: 100% !important;
}

/* Adjust grid layout for full width */
.lg\:grid {
  width: 100% !important;
  max-width: 100% !important;
}

/* Ensure content sections take full width */
.lg\:col-span-2,
.lg\:col-span-1 {
  width: 100% !important;
  max-width: 100% !important;
}

/* Custom card styles with !important */
.content-card {
  padding: 20px !important;
  margin: 0 8px 24px 8px !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 12px 32px 12px !important;
  }
}

.header-section {
  margin-bottom: 24px !important;
  padding: 0 16px !important;
  text-align: center !important;
}

@media (min-width: 640px) {
  .header-section {
    margin-bottom: 48px !important;
    padding: 0 24px !important;
  }
}

@media (min-width: 1024px) {
  .header-section {
    text-align: left !important;
  }
}

.exam-selection-card {
  background-color: white !important;
}

.info-panel-card,
.fun-facts-card {
  background-color: rgba(243, 244, 246, 0.8) !important;
  backdrop-filter: blur(8px) !important;
}

/* Ensure content spacing within cards */
.content-card > *:not(:last-child) {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card > *:not(:last-child) {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for nested elements */
.content-card h2,
.content-card h3 {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card h2,
  .content-card h3 {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for lists and other elements */
.content-card ul,
.content-card ol {
  margin: 12px 0 !important;
  padding-left: 24px !important;
}

.content-card li {
  margin-bottom: 8px !important;
}

/* Ensure proper spacing for buttons and interactive elements */
.content-card button {
  margin: 8px 0 !important;
}

/* Ensure proper spacing for form elements */
.content-card input,
.content-card select {
  margin-bottom: 12px !important;
}

/* Ensure proper spacing for the last element in cards */
.content-card > *:last-child {
  margin-bottom: 0 !important;
}

/* Ensure consistent grid layout */
@media (min-width: 1024px) {
  .lg\:grid {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 30px !important;
    padding: 0 15px !important;
    box-sizing: border-box !important;
  }

  .lg\:col-span-2 {
    grid-column: span 2 / span 2 !important;
  }
}

/* Adjust content card margins to align with grid */
.content-card {
  padding: 20px !important;
  margin: 0 0 24px 0 !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 0 32px 0 !important;
  }
}

/* Ensure the container takes full width */
.w-full {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* Custom border radius for all elements */
.content-card,
.header-section,
.exam-selection-card,
.info-panel-card,
.fun-facts-card,
.input-ios,
.select-ios,
button,
.rounded-lg,
.rounded-xl,
.rounded-2xl,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80,
.rounded-full {
  border-radius: 20px !important;
}

/* Specific adjustments for smaller elements */
.input-ios,
.select-ios,
button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 20px !important;
}

/* Ensure consistent border radius in modals */
:deep(.program-details-modal) {
  --border-radius: 20px 20px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 20px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .content-card,
  .header-section,
  .exam-selection-card,
  .info-panel-card,
  .fun-facts-card,
  :deep(.program-details-modal) {
    border-radius: 20px !important;
  }
}

/* Override any Tailwind border radius classes */
.rounded-lg,
.rounded-xl,
.rounded-2xl,
.rounded-full {
  border-radius: 20px !important;
}

/* Ensure buttons and inputs maintain the border radius */
button,
input,
select,
.input-ios,
.select-ios {
  border-radius: 20px !important;
}

/* Segment Container for Scrolling */
.segment-container {
  width: 100% !important;
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
  padding-bottom: 4px !important;
  position: relative !important;
}

.segment-container::-webkit-scrollbar {
  display: none !important;
}

/* Add shadow to indicate scrollable content */
.segment-container::after {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 40px !important;
  background: linear-gradient(to right, 
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 1) 100%
  ) !important;
  pointer-events: none !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  z-index: 10 !important;
}

.segment-container.has-content::after {
  opacity: 1 !important;
}

:deep(.custom-segment) {
  display: inline-flex !important;
  min-width: min-content !important;
  width: auto !important;
  overflow: visible !important;
  padding-right: 16px !important; /* Add padding to the right of the segment */
}

:deep(.custom-segment ion-segment-button) {
  white-space: nowrap !important;
}

@media (max-width: 640px) {
  .segment-container {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }

  :deep(.custom-segment) {
    padding-right: 8px !important; /* Reduce right padding on mobile */
  }
}

/* Custom Campus Segment */
.campus-segment-container {
  display: block !important;
  overflow-x: auto !important;
  white-space: nowrap !important;
  padding: 4px !important;
  background-color: rgba(235, 235, 245, 0.8) !important;
  border-radius: 12px !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important; /* Firefox */
}
.campus-segment-container::-webkit-scrollbar {
  display: none !important; /* Safari and Chrome */
}

.campus-segment-button {
  display: inline-block !important;
  padding: 8px 16px !important;
  margin: 0 2px !important;
  border: none !important;
  background-color: transparent !important;
  color: rgb(60, 60, 67, 0.6) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  border-radius: 9px !important;
  cursor: pointer !important;
  transition: all 0.2s ease-in-out !important;
}

.campus-segment-button.is-active {
  background-color: white !important;
  color: rgb(0, 122, 255) !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

/* Mobile-specific adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 28px !important;
    line-height: 1.2 !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
    line-height: 1.3 !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen p {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  .min-h-screen label {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen button {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen input,
  .min-h-screen select {
    font-size: 14px !important;
    line-height: 1.4 !important;
    padding: 8px !important;
  }

  /* Adjust card spacing and padding */
  .rounded-xl {
    border-radius: 12px !important;
  }

  .p-4 {
    padding: 16px !important;
  }

  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 16px !important;
  }

  /* Adjust grid gap */
  .gap-3 {
    gap: 12px !important;
  }

  /* Adjust icon sizes */
  .w-5 {
    width: 20px !important;
  }

  .h-5 {
    height: 20px !important;
  }

  .w-6 {
    width: 24px !important;
  }

  .h-6 {
    height: 24px !important;
  }
}

/* Adjust modal styles for mobile */
@media (max-width: 640px) {
  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }

  :deep(.program-details-modal ion-header) {
    padding: 12px;
  }

  :deep(.program-details-modal ion-content) {
    --padding-top: 16px;
    --padding-bottom: 16px;
    --padding-start: 16px;
    --padding-end: 16px;
  }

  :deep(.program-details-modal h3) {
    font-size: 19px !important;
  }

  :deep(.program-details-modal p) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-xs) {
    font-size: 13px !important;
  }

  :deep(.program-details-modal .text-sm) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-base) {
    font-size: 17px !important;
  }
}

/* Adjust segment styles for mobile */
@media (max-width: 640px) {
  :deep(.custom-segment) {
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 4px;
    --padding-end: 4px;
  }

  :deep(.custom-segment ion-segment-button) {
    font-size: 14px !important;
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 8px;
    --padding-end: 8px;
  }
}

/* Mobile-specific input styles */
@media (max-width: 640px) {
  .input-ios,
  .select-ios {
    padding: 10px 14px !important;
    min-height: 44px !important;
    font-size: 14px !important;
  }

  .input-ios::placeholder {
    font-size: 14px !important;
  }

  .select-ios {
    background-position: right 12px center !important;
    background-size: 14px !important;
    padding-right: 36px !important;
  }

  /* Adjust label spacing */
  label {
    margin-bottom: 6px !important;
  }

  /* Make buttons more touch-friendly */
  button {
    min-height: 44px !important;
    padding: 10px 16px !important;
  }

  /* Adjust spacing between exam elements */
  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 12px !important;
  }

  .gap-3 {
    gap: 12px !important;
  }
}

/* Base border radius styles */
.min-h-screen .rounded-lg {
  border-radius: 8px !important;
}

.min-h-screen .rounded-xl {
  border-radius: 12px !important;
}

.min-h-screen .rounded-2xl {
  border-radius: 16px !important;
}

/* Input and select specific styles */
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  border-radius: 8px !important;
}

/* Button specific styles */
.min-h-screen button {
  border-radius: 8px !important;
}

/* Card specific styles */
.min-h-screen .bg-white {
  border-radius: 16px !important;
}

/* Modal specific styles */
:deep(.program-details-modal) {
  --border-radius: 16px 16px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 16px !important;
}

/* Segment specific styles */
:deep(.custom-segment) {
  --border-radius: 8px !important;
}

:deep(.custom-segment ion-segment-button) {
  border-radius: 8px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl {
    border-radius: 12px !important;
  }

  .min-h-screen .rounded-2xl {
    border-radius: 14px !important;
  }

  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }
}

/* Exam entry styles */
.bg-gray-50\/50 {
  background-color: rgba(249, 250, 251, 0.5) !important;
  backdrop-filter: blur(8px) !important;
}

.border-gray-100 {
  border-color: rgba(243, 244, 246, 0.8) !important;
}

/* Add hover effect to exam entries */
.bg-gray-50\/50:hover {
  background-color: rgba(249, 250, 251, 0.7) !important;
  border-color: rgba(229, 231, 235, 0.8) !important;
  transition: all 0.2s ease-in-out;
}

/* Compact exam entry layout */
@media (min-width: 640px) {
  .exam-entry-row {
    flex-direction: row !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 0 !important;
  }
}
@media (max-width: 639px) {
  .exam-entry-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 8px !important;
  }
}

/* Special border radius class with !important */
.force-radius {
  border-radius: 17px !important;
}

.force-radius-sm {
  border-radius: 12px !important;
}

/* Apply to all elements that need border radius */
.min-h-screen .rounded-lg,
.min-h-screen .rounded-xl,
.min-h-screen .rounded-2xl,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
.min-h-screen .bg-white,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80 {
  border-radius: 17px !important;
}

/* Smaller radius for some elements */
.min-h-screen .rounded-lg,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 12px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl,
  .min-h-screen .rounded-2xl,
  :deep(.program-details-modal) {
    border-radius: 14px !important;
  }
}

/* Force all text to be 16px */
.min-h-screen,
.min-h-screen *,
:deep(.program-details-modal),
:deep(.program-details-modal *) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Specific adjustments for headings */
.min-h-screen h1 {
  font-size: 28px !important;
  line-height: 1.2 !important;
}

.min-h-screen h2 {
  font-size: 24px !important;
  line-height: 1.3 !important;
}

.min-h-screen h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

/* Input and select specific text size */
.min-h-screen input,
.min-h-screen select,
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Button text size */
.min-h-screen button {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Label text size */
.min-h-screen label {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Modal specific text sizes */
:deep(.program-details-modal) h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

:deep(.program-details-modal) p {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-xs {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-sm {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-base {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Segment button text size */
:deep(.custom-segment ion-segment-button) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 24px !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
  }

  .min-h-screen input,
  .min-h-screen select,
  .min-h-screen .input-ios,
  .min-h-screen .select-ios,
  .min-h-screen button,
  .min-h-screen label {
    font-size: 16px !important;
  }
}

/* Ensure full width for all sections */
.min-h-screen > div {
  width: 100% !important;
  max-width: 100% !important;
}

/* Adjust grid layout for full width */
.lg\:grid {
  width: 100% !important;
  max-width: 100% !important;
}

/* Ensure content sections take full width */
.lg\:col-span-2,
.lg\:col-span-1 {
  width: 100% !important;
  max-width: 100% !important;
}

/* Custom card styles with !important */
.content-card {
  padding: 20px !important;
  margin: 0 8px 24px 8px !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 12px 32px 12px !important;
  }
}

.header-section {
  margin-bottom: 24px !important;
  padding: 0 16px !important;
  text-align: center !important;
}

@media (min-width: 640px) {
  .header-section {
    margin-bottom: 48px !important;
    padding: 0 24px !important;
  }
}

@media (min-width: 1024px) {
  .header-section {
    text-align: left !important;
  }
}

.exam-selection-card {
  background-color: white !important;
}

.info-panel-card,
.fun-facts-card {
  background-color: rgba(243, 244, 246, 0.8) !important;
  backdrop-filter: blur(8px) !important;
}

/* Ensure content spacing within cards */
.content-card > *:not(:last-child) {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card > *:not(:last-child) {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for nested elements */
.content-card h2,
.content-card h3 {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card h2,
  .content-card h3 {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for lists and other elements */
.content-card ul,
.content-card ol {
  margin: 12px 0 !important;
  padding-left: 24px !important;
}

.content-card li {
  margin-bottom: 8px !important;
}

/* Ensure proper spacing for buttons and interactive elements */
.content-card button {
  margin: 8px 0 !important;
}

/* Ensure proper spacing for form elements */
.content-card input,
.content-card select {
  margin-bottom: 12px !important;
}

/* Ensure proper spacing for the last element in cards */
.content-card > *:last-child {
  margin-bottom: 0 !important;
}

/* Ensure consistent grid layout */
@media (min-width: 1024px) {
  .lg\:grid {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 30px !important;
    padding: 0 15px !important;
    box-sizing: border-box !important;
  }

  .lg\:col-span-2 {
    grid-column: span 2 / span 2 !important;
  }
}

/* Adjust content card margins to align with grid */
.content-card {
  padding: 20px !important;
  margin: 0 0 24px 0 !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 0 32px 0 !important;
  }
}

/* Ensure the container takes full width */
.w-full {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* Custom border radius for all elements */
.content-card,
.header-section,
.exam-selection-card,
.info-panel-card,
.fun-facts-card,
.input-ios,
.select-ios,
button,
.rounded-lg,
.rounded-xl,
.rounded-2xl,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80,
.rounded-full {
  border-radius: 20px !important;
}

/* Specific adjustments for smaller elements */
.input-ios,
.select-ios,
button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 20px !important;
}

/* Ensure consistent border radius in modals */
:deep(.program-details-modal) {
  --border-radius: 20px 20px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 20px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .content-card,
  .header-section,
  .exam-selection-card,
  .info-panel-card,
  .fun-facts-card,
  :deep(.program-details-modal) {
    border-radius: 20px !important;
  }
}

/* Override any Tailwind border radius classes */
.rounded-lg,
.rounded-xl,
.rounded-2xl,
.rounded-full {
  border-radius: 20px !important;
}

/* Ensure buttons and inputs maintain the border radius */
button,
input,
select,
.input-ios,
.select-ios {
  border-radius: 20px !important;
}

/* Segment Container for Scrolling */
.segment-container {
  width: 100% !important;
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
  padding-bottom: 4px !important;
  position: relative !important;
}

.segment-container::-webkit-scrollbar {
  display: none !important;
}

/* Add shadow to indicate scrollable content */
.segment-container::after {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 40px !important;
  background: linear-gradient(to right, 
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 1) 100%
  ) !important;
  pointer-events: none !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  z-index: 10 !important;
}

.segment-container.has-content::after {
  opacity: 1 !important;
}

:deep(.custom-segment) {
  display: inline-flex !important;
  min-width: min-content !important;
  width: auto !important;
  overflow: visible !important;
  padding-right: 16px !important; /* Add padding to the right of the segment */
}

:deep(.custom-segment ion-segment-button) {
  white-space: nowrap !important;
}

@media (max-width: 640px) {
  .segment-container {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }

  :deep(.custom-segment) {
    padding-right: 8px !important; /* Reduce right padding on mobile */
  }
}

/* Custom Campus Segment */
.campus-segment-container {
  display: block !important;
  overflow-x: auto !important;
  white-space: nowrap !important;
  padding: 4px !important;
  background-color: rgba(235, 235, 245, 0.8) !important;
  border-radius: 12px !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important; /* Firefox */
}
.campus-segment-container::-webkit-scrollbar {
  display: none !important; /* Safari and Chrome */
}

.campus-segment-button {
  display: inline-block !important;
  padding: 8px 16px !important;
  margin: 0 2px !important;
  border: none !important;
  background-color: transparent !important;
  color: rgb(60, 60, 67, 0.6) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  border-radius: 9px !important;
  cursor: pointer !important;
  transition: all 0.2s ease-in-out !important;
}

.campus-segment-button.is-active {
  background-color: white !important;
  color: rgb(0, 122, 255) !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

/* Mobile-specific adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 28px !important;
    line-height: 1.2 !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
    line-height: 1.3 !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen p {
    font-size: 16px !important;
    line-height: 1.5 !important;
  }

  .min-h-screen label {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen button {
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  .min-h-screen input,
  .min-h-screen select {
    font-size: 14px !important;
    line-height: 1.4 !important;
    padding: 8px !important;
  }

  /* Adjust card spacing and padding */
  .rounded-xl {
    border-radius: 12px !important;
  }

  .p-4 {
    padding: 16px !important;
  }

  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 16px !important;
  }

  /* Adjust grid gap */
  .gap-3 {
    gap: 12px !important;
  }

  /* Adjust icon sizes */
  .w-5 {
    width: 20px !important;
  }

  .h-5 {
    height: 20px !important;
  }

  .w-6 {
    width: 24px !important;
  }

  .h-6 {
    height: 24px !important;
  }
}

/* Adjust modal styles for mobile */
@media (max-width: 640px) {
  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }

  :deep(.program-details-modal ion-header) {
    padding: 12px;
  }

  :deep(.program-details-modal ion-content) {
    --padding-top: 16px;
    --padding-bottom: 16px;
    --padding-start: 16px;
    --padding-end: 16px;
  }

  :deep(.program-details-modal h3) {
    font-size: 19px !important;
  }

  :deep(.program-details-modal p) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-xs) {
    font-size: 13px !important;
  }

  :deep(.program-details-modal .text-sm) {
    font-size: 15px !important;
  }

  :deep(.program-details-modal .text-base) {
    font-size: 17px !important;
  }
}

/* Adjust segment styles for mobile */
@media (max-width: 640px) {
  :deep(.custom-segment) {
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 4px;
    --padding-end: 4px;
  }

  :deep(.custom-segment ion-segment-button) {
    font-size: 14px !important;
    --padding-top: 4px;
    --padding-bottom: 4px;
    --padding-start: 8px;
    --padding-end: 8px;
  }
}

/* Mobile-specific input styles */
@media (max-width: 640px) {
  .input-ios,
  .select-ios {
    padding: 10px 14px !important;
    min-height: 44px !important;
    font-size: 14px !important;
  }

  .input-ios::placeholder {
    font-size: 14px !important;
  }

  .select-ios {
    background-position: right 12px center !important;
    background-size: 14px !important;
    padding-right: 36px !important;
  }

  /* Adjust label spacing */
  label {
    margin-bottom: 6px !important;
  }

  /* Make buttons more touch-friendly */
  button {
    min-height: 44px !important;
    padding: 10px 16px !important;
  }

  /* Adjust spacing between exam elements */
  .space-y-4 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 12px !important;
  }

  .gap-3 {
    gap: 12px !important;
  }
}

/* Base border radius styles */
.min-h-screen .rounded-lg {
  border-radius: 8px !important;
}

.min-h-screen .rounded-xl {
  border-radius: 12px !important;
}

.min-h-screen .rounded-2xl {
  border-radius: 16px !important;
}

/* Input and select specific styles */
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  border-radius: 8px !important;
}

/* Button specific styles */
.min-h-screen button {
  border-radius: 8px !important;
}

/* Card specific styles */
.min-h-screen .bg-white {
  border-radius: 16px !important;
}

/* Modal specific styles */
:deep(.program-details-modal) {
  --border-radius: 16px 16px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 16px !important;
}

/* Segment specific styles */
:deep(.custom-segment) {
  --border-radius: 8px !important;
}

:deep(.custom-segment ion-segment-button) {
  border-radius: 8px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl {
    border-radius: 12px !important;
  }

  .min-h-screen .rounded-2xl {
    border-radius: 14px !important;
  }

  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }
}

/* Exam entry styles */
.bg-gray-50\/50 {
  background-color: rgba(249, 250, 251, 0.5) !important;
  backdrop-filter: blur(8px) !important;
}

.border-gray-100 {
  border-color: rgba(243, 244, 246, 0.8) !important;
}

/* Add hover effect to exam entries */
.bg-gray-50\/50:hover {
  background-color: rgba(249, 250, 251, 0.7) !important;
  border-color: rgba(229, 231, 235, 0.8) !important;
  transition: all 0.2s ease-in-out;
}

/* Compact exam entry layout */
@media (min-width: 640px) {
  .exam-entry-row {
    flex-direction: row !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 0 !important;
  }
}
@media (max-width: 639px) {
  .exam-entry-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .exam-entry-row > div {
    margin-bottom: 8px !important;
  }
}

/* Special border radius class with !important */
.force-radius {
  border-radius: 17px !important;
}

.force-radius-sm {
  border-radius: 12px !important;
}

/* Apply to all elements that need border radius */
.min-h-screen .rounded-lg,
.min-h-screen .rounded-xl,
.min-h-screen .rounded-2xl,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
.min-h-screen .bg-white,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80 {
  border-radius: 17px !important;
}

/* Smaller radius for some elements */
.min-h-screen .rounded-lg,
.min-h-screen .input-ios,
.min-h-screen .select-ios,
.min-h-screen button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 12px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl,
  .min-h-screen .rounded-2xl,
  :deep(.program-details-modal) {
    border-radius: 14px !important;
  }
}

/* Force all text to be 16px */
.min-h-screen,
.min-h-screen *,
:deep(.program-details-modal),
:deep(.program-details-modal *) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Specific adjustments for headings */
.min-h-screen h1 {
  font-size: 28px !important;
  line-height: 1.2 !important;
}

.min-h-screen h2 {
  font-size: 24px !important;
  line-height: 1.3 !important;
}

.min-h-screen h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

/* Input and select specific text size */
.min-h-screen input,
.min-h-screen select,
.min-h-screen .input-ios,
.min-h-screen .select-ios {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Button text size */
.min-h-screen button {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Label text size */
.min-h-screen label {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Modal specific text sizes */
:deep(.program-details-modal) h3 {
  font-size: 20px !important;
  line-height: 1.4 !important;
}

:deep(.program-details-modal) p {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-xs {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-sm {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

:deep(.program-details-modal) .text-base {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Segment button text size */
:deep(.custom-segment ion-segment-button) {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .min-h-screen h1 {
    font-size: 24px !important;
  }

  .min-h-screen h2 {
    font-size: 20px !important;
  }

  .min-h-screen h3 {
    font-size: 18px !important;
  }

  .min-h-screen input,
  .min-h-screen select,
  .min-h-screen .input-ios,
  .min-h-screen .select-ios,
  .min-h-screen button,
  .min-h-screen label {
    font-size: 16px !important;
  }
}

/* Ensure full width for all sections */
.min-h-screen > div {
  width: 100% !important;
  max-width: 100% !important;
}

/* Adjust grid layout for full width */
.lg\:grid {
  width: 100% !important;
  max-width: 100% !important;
}

/* Ensure content sections take full width */
.lg\:col-span-2,
.lg\:col-span-1 {
  width: 100% !important;
  max-width: 100% !important;
}

/* Custom card styles with !important */
.content-card {
  padding: 20px !important;
  margin: 0 8px 24px 8px !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 12px 32px 12px !important;
  }
}

.header-section {
  margin-bottom: 24px !important;
  padding: 0 16px !important;
  text-align: center !important;
}

@media (min-width: 640px) {
  .header-section {
    margin-bottom: 48px !important;
    padding: 0 24px !important;
  }
}

@media (min-width: 1024px) {
  .header-section {
    text-align: left !important;
  }
}

.exam-selection-card {
  background-color: white !important;
}

.info-panel-card,
.fun-facts-card {
  background-color: rgba(243, 244, 246, 0.8) !important;
  backdrop-filter: blur(8px) !important;
}

/* Ensure content spacing within cards */
.content-card > *:not(:last-child) {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card > *:not(:last-child) {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for nested elements */
.content-card h2,
.content-card h3 {
  margin-bottom: 16px !important;
}

@media (min-width: 640px) {
  .content-card h2,
  .content-card h3 {
    margin-bottom: 24px !important;
  }
}

/* Ensure proper spacing for lists and other elements */
.content-card ul,
.content-card ol {
  margin: 12px 0 !important;
  padding-left: 24px !important;
}

.content-card li {
  margin-bottom: 8px !important;
}

/* Ensure proper spacing for buttons and interactive elements */
.content-card button {
  margin: 8px 0 !important;
}

/* Ensure proper spacing for form elements */
.content-card input,
.content-card select {
  margin-bottom: 12px !important;
}

/* Ensure proper spacing for the last element in cards */
.content-card > *:last-child {
  margin-bottom: 0 !important;
}

/* Ensure consistent grid layout */
@media (min-width: 1024px) {
  .lg\:grid {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 30px !important;
    padding: 0 15px !important;
    box-sizing: border-box !important;
  }

  .lg\:col-span-2 {
    grid-column: span 2 / span 2 !important;
  }
}

/* Adjust content card margins to align with grid */
.content-card {
  padding: 20px !important;
  margin: 0 0 24px 0 !important;
  border-radius: 16px !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(229, 231, 235, 0.8) !important;
}

@media (min-width: 640px) {
  .content-card {
    padding: 28px !important;
    margin: 0 0 32px 0 !important;
  }
}

/* Ensure the container takes full width */
.w-full {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* Custom border radius for all elements */
.content-card,
.header-section,
.exam-selection-card,
.info-panel-card,
.fun-facts-card,
.input-ios,
.select-ios,
button,
.rounded-lg,
.rounded-xl,
.rounded-2xl,
:deep(.program-details-modal),
:deep(.program-details-modal .rounded-2xl),
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button),
.bg-gray-50\/50,
.bg-blue-50,
.bg-white\/80,
.rounded-full {
  border-radius: 20px !important;
}

/* Specific adjustments for smaller elements */
.input-ios,
.select-ios,
button,
:deep(.custom-segment),
:deep(.custom-segment ion-segment-button) {
  border-radius: 20px !important;
}

/* Ensure consistent border radius in modals */
:deep(.program-details-modal) {
  --border-radius: 20px 20px 0 0 !important;
}

:deep(.program-details-modal .rounded-2xl) {
  border-radius: 20px !important;
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .content-card,
  .header-section,
  .exam-selection-card,
  .info-panel-card,
  .fun-facts-card,
  :deep(.program-details-modal) {
    border-radius: 20px !important;
  }
}

/* Override any Tailwind border radius classes */
.rounded-lg,
.rounded-xl,
.rounded-2xl,
.rounded-full {
  border-radius: 20px !important;
}

/* Ensure buttons and inputs maintain the border radius */
button,
input,
select,
.input-ios,
.select-ios {
  border-radius: 20px !important;
}

/* Segment Container for Scrolling */
.segment-container {
  width: 100% !important;
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
  padding-bottom: 4px !important;
  position: relative !important;
}

.segment-container::-webkit-scrollbar {
  display: none !important;
}

/* Add shadow to indicate scrollable content */
.segment-container::after {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 40px !important;
  background: linear-gradient(to right, 
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 1) 100%
  ) !important;
  pointer-events: none !important;
  opacity: 0 !important;
  transition: opacity 0.2s ease !important;
  z-index: 10 !important;
}

.segment-container.has-content::after {
  opacity: 1 !important;
}

:deep(.custom-segment) {
  display: inline-flex !important;
  min-width: min-content !important;
  width: auto !important;
  overflow: visible !important;
  padding-right: 16px !important; /* Add padding to the right of the segment */
}

:deep(.custom-segment ion-segment-button) {
  white-space: nowrap !important;
}

@media (max-width: 640px) {
  .segment-container {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }

  :deep(.custom-segment) {
    padding-right: 8px !important; /* Reduce right padding on mobile */
  }
}

/* Custom Campus Segment */
.campus-segment-container {
  display: block !important;
  overflow-x: auto !important;
  white-space: nowrap !important;
  padding: 4px !important;
  background-color: rgba(235, 235, 245, 0.8) !important;
  border-radius: 12px !important;
  -webkit-overflow-scrolling: touch !important;
  scrollbar-width: none !important; /* Firefox */
}
.campus-segment-container::-webkit-scrollbar {
  display: none !important; /* Safari and Chrome */
}

.campus-segment-button {
  display: inline-block !important;
  padding: 8px 16px !important;
  margin: 0 2px !important;
  border: none !important;
  background-color: transparent !important;
  color: rgb(60, 60, 67, 0.6) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  border-radius: 9px !important;
  cursor: pointer !important;
  transition: all 0.2s ease-in-out !important;
}

.campus-segment-button.is-active {
  background-color: white !important;
  color: rgb(0, 122, 255) !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

/* Mobile-specific adjustments */
@media (max-width: 640px) {
  .min-h-screen .rounded-xl {
    border-radius: 12px !important;
  }

  .min-h-screen .rounded-2xl {
    border-radius: 14px !important;
  }

  :deep(.program-details-modal) {
    --border-radius: 14px 14px 0 0 !important;
  }

  /* Custom campus dropdown styles for mobile */
  .campus-dropdown {
    transition: all 0.2s ease-in-out !important;
  }

  .campus-dropdown:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  }

  .campus-dropdown:focus {
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  }

  /* Reduce mobile padding for program cards */
  .grid.grid-cols-1.md\\:grid-cols-2 {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  @media (min-width: 640px) {
    .grid.grid-cols-1.md\\:grid-cols-2 {
      padding-left: 4px !important;
      padding-right: 4px !important;
    }
  }
}

</style>
